/**
 * ScansByTime2 — time-series chart for the Analytics page.
 *
 * The Certificate Status filter has been moved to the global AnalyticsFiltersHeader.
 * This component receives `globalCertificateStatuses` as a prop and uses it to
 * scope the API call. When empty, all statuses are included (no filter).
 *
 * Slice 4 (VERA-265): recharts Brush for span-select/zoom, brand-purple gradient
 * area fill, axis polish, and animation.
 *
 * THE BRUSH IS A LOCAL VIEW WINDOW, NOT A PAGE FILTER (VERA-260).
 * ---------------------------------------------------------------
 * It used to `dispatch(setAnalyticsFilters({ dateRange }))` on every `onChange`,
 * which produced three defects that compounded:
 *
 *  1. A RATCHET with no inverse. The dispatch narrowed the global range;
 *     `AnalyticsPage` fed that straight back down as `dateFrom`/`dateTo`;
 *     `useAnalyticsStatistics` then refetched ONLY that window; and because the
 *     `<Brush>` was uncontrolled it re-spanned 100% of the new, narrower data. The
 *     selection had become the domain, so no gesture existed that widened it again.
 *  2. A BURST of writes. recharts fires `onChange` continuously while a traveller is
 *     dragged, and nothing debounced it, so one drag re-keyed the statistics, totals,
 *     ROI and map queries dozens of times.
 *  3. SILENT CROSS-SURFACE MUTATION. `filters.dateRange` also scopes the KPI row above
 *     this card and the map below it, so dragging a slider inside one card rewrote the
 *     page's headline numbers with no undo.
 *
 * So: the top-level date picker is now the SOLE writer of `filters.dateRange`. The
 * brush is controlled from local state and changes nothing outside this card — which
 * is what makes zoom-OUT possible again, since the underlying data stops moving
 * underneath the selection. Promoting a window to the page is an explicit, one-shot
 * click ("Apply to page"), never a side effect of the drag.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback, useSyncExternalStore } from 'react'
import { useDispatch } from 'react-redux'

import { DEFAULT_COMPANY_ID } from 'config'
import { CERTIFICATE_STATUSES } from 'constants/statuses'

import { date, time } from 'utils'

import { useAnalyticsStatistics } from 'hooks/api'
import {
  ComposedChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Brush,
} from 'recharts'

import { type AnalyticsValueDimension } from 'store/analytics/types'

import {
  // The bucket width (hour / day / 7-day / month), shared with the CSV export so the two
  // cannot ask the API for different slicings of the same range. See the note above
  // `ScansByTimeProps` for why this is imported as a FUNCTION and not as a threshold.
  analyticsBucketRequest,
  analyticsEffectiveBucketUnit,
  // The unit a value series is denominated in. Imported rather than re-declared:
  // `VALUE_UNIT` there is TOTAL over the value dimensions, so a new one is a
  // compile error until it is given a unit — a local copy would silently diverge.
  type AnalyticsValueUnit,
  analyticsValueUnit,
  analyticsValueTerms,
  analyticsValueReconstruction,
  isPlottableStatistic,
  analyticsValueLabel,
  ANALYTICS_MIN_STATISTIC_DENOMINATOR,
} from 'components/analytics/analyticsDisplay'

// THIS CARD's bucket preference, keyed by `cardId` — not a page-level filter (VRG-512).
// Direct file import, not the barrel: the barrel re-exports `analyticsExport`, which is a
// heavier module this chart has no reason to pull in.
import { useAnalyticsCardGranularity } from 'components/analytics/analyticsCardGranularity'
// The house chart tokens, also by direct file import for the same reason.
import {
  CHART_AREA_PROPS,
  CHART_AXIS_PROPS,
  CHART_AXIS_TICK_FONT_SIZE,
  CHART_BAR_PROPS,
  ChartAreaGradient,
  chartActiveDot,
  chartAxisTick,
  chartGridProps,
  chartTooltipCursor,
  chartTooltipStyle,
} from 'components/analytics/chartStyle'

import { ChartColumnIcon, ChartLineIcon } from 'lucide-react'

import { Button } from '@repo/ui'
// Direct file import, not the barrel — the legend's pill is the SAME component the
// filters drawer renders, so the two cannot drift (§36).
import { StatusPill } from 'components/display/StatusPill/StatusPill'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'

import { Circle } from '@mui/icons-material'
import { Box, Typography, Stack, Skeleton, useTheme } from '@mui/material'
import type { Theme } from '@mui/material'

import { setAnalyticsFilters } from 'store/analytics/actions'
import type {
  AnalyticsSeriesCountField,
  AnalyticsSeriesDimension,
} from 'store/analytics/types'

/**
 * ONE SERIES OR N — the chart changes shape, not just colour.
 * ----------------------------------------------------------
 * BillingOverview plots a single series, so a straight copy of it is not available
 * here: this chart plots one series per certificate status. The resolution is that
 * the two cases are genuinely different questions and get different marks.
 *
 *  - NO status filter → the reader is asking "how much activity". That is ONE
 *    number per bucket, so it draws as ONE `brand[500]` gradient area — identical
 *    to the billing chart, down to the stops. It used to draw all NINE statuses as
 *    nine lines, eight of which are flat zero for most brands: a thicket that
 *    answers a question nobody asked and buries the total, which is the answer.
 *  - Statuses ARE selected → the reader has explicitly asked to COMPARE them, so
 *    several series is correct. They draw as lines with no fill, because N
 *    overlapping gradient fills occlude each other and the topmost one wins by
 *    z-order rather than by magnitude.
 *
 * WHY NOT THE STATUS CHIP COLOURS (the previous behaviour). `CERTIFICATE_STATUSES`
 * carries a semantic palette built for CHIPS — green "Active", red "Stolen", on
 * tinted backgrounds at the 700 step. Those are status tokens, and the dataviz rule
 * is that status colours are reserved: reusing them as a series palette means the
 * chart's identity channel is spoken for by meaning it isn't encoding, and it puts
 * two statuses that share a chip colour (Created and Inactive are both `gray[700]`)
 * on two indistinguishable lines. A categorical palette answers a different
 * question — "which line is which" — and is chosen for separability, not sentiment.
 *
 * WHY BARS AREN'T THE ANSWER HERE, though they are on the billing chart. That chart
 * switches to bars for MONTHLY buckets, because a smoothed curve through six
 * discrete monthly totals interpolates values that never existed. This surface only
 * ever buckets by hour (`period === '1'`) or by day, both of which are dense and
 * contiguous, so the line between two points is a real statement about the interval
 * between them. The trend mark is correct; the reasoning is the same one, applied to
 * a different granularity.
 */

/**
 * SERIES IDENTITY IS THE LABEL, NOT THE STATUS KEY.
 *
 * `CERTIFICATE_STATUSES` has nine keys but only SIX labels — the four transfer
 * states (`transfer_initiated`, `transfer_waiting_confirmation`, `waiting_accept`,
 * `cancel_request`) all read "Transfer", deliberately: that file states the
 * distinction between them is internal and not something the reader of a status
 * column can act on. Plotting them as four separately-coloured lines would have
 * spent four palette slots on a distinction the legend then refuses to name — four
 * legend rows reading "Transfer" in four different colours. They sum into one
 * series instead.
 *
 * The collapse is also what makes the palette legal. Nine series needs nine hues,
 * and no nine steps drawn from this theme's ramps clear the CVD floor against each
 * other (the best 8-hue set collapses orange against yellow at ΔE 0.1 under
 * protanopia — indistinguishable). Six clears it with margin.
 */
const STATUS_SERIES = (() => {
  const byLabel = new Map<string, { label: string; keys: string[] }>()
  Object.entries(CERTIFICATE_STATUSES).forEach(([value, { label }]) => {
    const existing = byLabel.get(label)
    if (existing) existing.keys.push(value)
    else byLabel.set(label, { label, keys: [value] })
  })
  // `slot` is the index into the categorical palette and is derived from the
  // declaration order of CERTIFICATE_STATUSES, so a status wears the SAME colour
  // whatever subset the user has filtered to. Keying colour off the position in the
  // filtered list would repaint every surviving series each time the filter changed.
  return Array.from(byLabel.values()).map((s, slot) => ({ ...s, dataKey: `s${slot}`, slot }))
})()

/**
 * ── EACH SERIES WEARS ITS OWN STATUS'S HUE ─────────────────────────────────────────
 *
 * One vocabulary, one colour language. Before this the chart drew a generic categorical
 * ramp — brand purple, cyan, green, orange, pink, red — assigned by declaration order,
 * so "Transfer" was orange on the chart and BLUE on every certificate row, and "Lost" was
 * pink on the chart and orange everywhere else. A reader who had learned the status chips
 * had to learn a second, contradictory mapping to read this card, and the legend was the
 * only thing reconciling them.
 *
 * ## THE STEP IS 600, THE CHIP'S IS 700 — and that difference is the point
 *
 * A chip is 700 text on a 100 tint: it is read against a pale ground, at a size where
 * weight matters more than chroma. A chart series is a 2px stroke on the card surface, in
 * both themes. Lifting the chip's own hex onto the line was tried and is wrong — those
 * steps are tuned for the tinted ground and several of them fail the lightness band
 * against the dark card (`gray[700]` measures OKLCH L 0.421 against a 0.48–0.67 dark band,
 * and 2.56:1 contrast on `#18181b`, i.e. a line you cannot see). So the FAMILY is
 * inherited and the STEP is chosen for the mark. Same hue, right value.
 *
 * ## THE ONE PLACE THE BADGES CANNOT BE FOLLOWED: Created and Inactive are BOTH gray
 *
 * `CERTIFICATE_STATUSES` gives them the identical `gray[700]`, because in a status column
 * colour is not the identity channel — the label is, and two neutral chips reading
 * "Created" and "Inactive" are unambiguous. On a chart colour IS the identity channel, so
 * two identical grays would be two lines nobody can tell apart. They therefore take two
 * STEPS of the same gray ramp: same family as their badge, separated by lightness, which
 * is the one channel every form of colour-vision deficiency preserves.
 *
 * The assignment between them (Created lighter, Inactive darker) is arbitrary and is
 * recorded as such — the badge system does not rank them, so neither can this.
 *
 * ## VALIDATED, NOT EYEBALLED — AND THE RESULT IS NOT A CLEAN PASS
 *
 * ⚠️ The theme's `gray` ramp is REPLACED WHOLESALE IN DARK MODE (`@repo/ui`'s `darkNeutral`
 * maps it onto shadcn zinc and REVERSES it, so a high index is light rather than dark).
 * The four chromatic ramps are identical across modes; the neutrals are not. So the two
 * modes had to be validated against different hexes, and a single-mode check would have
 * been worthless here — this was caught by reading the RENDERED stroke off the page, not
 * from the constants file.
 *
 *   Created  gray[400]   light #81858D   dark #71717a
 *   Inactive gray[500]   light #60646B   dark #a1a1aa
 *
 * Through the dataviz validator, ALL PAIRS (not adjacent — on a multi-line chart any two
 * lines can end up side by side), against the real card surfaces:
 *
 *   light (#ffffff)  band PASS · all-pairs CVD PASS (worst 13.2, the two grays) · contrast PASS
 *   dark  (#0f0f12)  band FAIL on #a1a1aa · all-pairs CVD PASS (worst 15.4) · contrast PASS
 *
 * TWO reported failures, and they are different in kind:
 *
 *  1. CHROMA FLOOR, on both neutrals ("below floor: reads gray"). ACCEPTED BY CONSTRUCTION.
 *     That check exists to catch a HUE so desaturated it reads neutral by accident; here
 *     neutral is the correct answer, because it is what the badge says. The separability
 *     the floor is a proxy for is measured directly and passes with margin.
 *
 *  2. ⚠️ LIGHTNESS BAND, dark mode only: `Inactive` renders `#a1a1aa`, OKLCH L 0.712
 *     against a 0.48–0.67 band. This is a REAL defect and is documented rather than
 *     hidden. Its concrete consequence is that `#a1a1aa` is also the dark theme's
 *     `text.secondary`, so that one series is drawn in the same value as muted body text —
 *     the "an INK token is not a series colour" objection `BillingOverview` records about
 *     `primary.main`, in a milder form.
 *
 *     It is NOT fixable inside the ramp, which is why it ships named. The dark neutral ramp
 *     offers exactly ONE in-band step: `#71717a` passes, `#52525b` (gray[300]) fails both
 *     the band (0.442) and contrast (2.48:1), and `#a1a1aa`/`#d4d4d8` are above the band.
 *     Two badge-faithful neutrals therefore cannot both be legal in dark mode. The
 *     alternative was to give one of Created/Inactive an unrelated hue, which breaks the
 *     one thing this change exists to fix.
 *
 *     What is NOT wrong with it: the two are comfortably distinguishable. All-pairs CVD in
 *     dark reports its worst pair as orange↔green at 15.4, so the gray pair separates by
 *     MORE than that under every simulated deficiency. This is a token-collision problem,
 *     not a "two indistinguishable lines" problem.
 *
 *     The fix belongs in the ramp: a second in-band dark neutral (roughly `#8a8a94`, which
 *     validates PASS on band and contrast) would close it for every chart, not just this
 *     one. Out of scope for this file — a literal hex here would be exactly the drift this
 *     block is about.
 *
 * ⚠️ Keyed by STATUS KEY, and the `Record` is exhaustive over `CERTIFICATE_STATUSES`. That
 * is deliberate: adding a status to that file without deciding its chart hue becomes a
 * TypeScript error here rather than a silent fallback to some default nobody chose.
 */
const STATUS_CHART_HUE: Record<keyof typeof CERTIFICATE_STATUSES, (t: Theme) => string> = {
  // Both gray, per their badges; two steps apart so they are separable as lines.
  created: t => t.palette.gray[400],
  inactive: t => t.palette.gray[500],
  active: t => t.palette.green[600],
  // All four in-flight transfer states collapse into ONE series, so all four map to the
  // one hue their shared "Transfer" chip already uses.
  transfer_initiated: t => t.palette.blue[600],
  transfer_waiting_confirmation: t => t.palette.blue[600],
  waiting_accept: t => t.palette.blue[600],
  cancel_request: t => t.palette.blue[600],
  lost: t => t.palette.orange[600],
  stolen: t => t.palette.red[600],
}

/** Row key holding the all-status total — the single series drawn when nothing is filtered. */
const TOTAL_KEY = '_total'

/**
 * Row key holding a VALUE series' plotted point (VERA-260).
 *
 * A separate key from `TOTAL_KEY` rather than a reuse, because the two carry different
 * NULLABILITY contracts and recharts treats the difference as significant. `TOTAL_KEY` is
 * always a number — `?? 0` is correct for a count, since a bucket with no scans genuinely
 * had zero scans. A value point is `number | null`, and the null is load-bearing: it is
 * how the line breaks rather than dipping to a baseline nobody measured. Sharing one key
 * would put a `?? 0` and a deliberate null on the same accessor, and the `?? 0` would win
 * the next time someone tidied it.
 */
const VALUE_KEY = '_value'

/**
 * Row key holding the whole `AnalyticsBucketStatistic` for the hovered bucket.
 *
 * ⚠️ IT IS ON THE ROW EVEN WHEN THE POINT IS NOT PLOTTED, and that is the entire mechanism
 * for telling a SUPPRESSED bucket from an EMPTY one. Both render as a gap in the line, so
 * without this they would be indistinguishable — which would be the null-is-not-zero
 * defect all over again, one level up: "too few observations to plot" and "nothing
 * happened" are different facts and a bare gap asserts neither.
 *
 * recharts' `Tooltip` hands the whole row through as `payload[0].payload`, so a custom
 * tooltip can read this for the hovered bucket even for a series whose `dataKey` is null
 * there and which recharts therefore omits from its own item list.
 */
const STATISTIC_KEY = '_statistic'

/**
 * A composed series' row key, keyed by its POSITION in `composedSeries` — not by its
 * metric id. Position is what `analyticsGrid`'s own id grammar already treats as
 * identity ("ORDER IS SIGNIFICANT and is NOT normalised" — see `composeAnalyticsVariantCardId`),
 * so reusing it here rather than deriving a key from the metric id keeps the two
 * agreeing about what "the second series" means.
 */
const composedDataKey = (index: number) => `composed_${index}`

/**
 * The field on one statistics bucket holding a dimension's TOTAL count. The composed
 * series reads THIS, never the primary metric's per-status `_detailed` breakdown — see
 * the "NO SECOND FETCH" note on `ScansByTimeProps.composedSeries` for why the same
 * already-fetched response answers both metrics.
 */
const TOTAL_COUNT_FIELD: Record<AnalyticsSeriesDimension, AnalyticsSeriesCountField> = {
  scans: 'scans_count',
  certificates: 'certificates_count',
  transfers: 'transfers_count',
  invalid_scans: 'invalid_scans_count',
  stolen_reports: 'stolen_reports_count',
  lost_reports: 'lost_reports_count',
  resales: 'resales_count',
}

/**
 * ── WHICH DIMENSIONS HAVE A PER-STATUS BREAKDOWN, AND WHICH ARE A TOTAL ONLY ────────
 *
 * `null` is not "not implemented". A certificate STATUS is a fact about a certificate; it
 * is not a fact about a rejected scan, a completed resale or a loss report. "Invalid scans
 * that are Active" is not a measurement, so there is nothing for the status legend to
 * break these four into and the chart draws the single total series instead.
 *
 * ⚠️ THIS IS THE LOOKUP THAT REPLACED THE TERNARY, and replacing it is what made the four
 * new dimensions chartable at all. The old line —
 * `dataType === 'certificates' ? 'certificates_detailed' : 'scans_detailed'` — had no
 * failure mode for an unknown dimension: everything that was not `certificates` fell
 * through to SCANS. A resales chart would have read `scans_detailed`, summed a scan
 * breakdown into its total, and drawn a SCANS series under a resales title. That silent
 * wrong-answer branch is precisely why `analyticsDisplay.CHART_RENDERABLE` used to report
 * `transfers` as unplottable, and it is why this is a total `Record` rather than a
 * ternary with a default.
 */
const DETAILED_FIELD: Record<
  AnalyticsSeriesDimension,
  'scans_detailed' | 'certificates_detailed' | null
> = {
  scans: 'scans_detailed',
  certificates: 'certificates_detailed',
  /*
    `transfers_detailed` EXISTS on the bucket and is still `null` here on purpose: it is a
    per-COMPANY breakdown (`{ count, company_id }`), carrying no `status` at all, so the
    per-status accessor below would read `undefined` from every entry and bucket the lot
    under one `undefined` key. A breakdown the legend cannot express is not a breakdown.
  */
  transfers: null,
  invalid_scans: null,
  stolen_reports: null,
  lost_reports: null,
  resales: null,
}

/**
 * The noun a dimension's total series is labelled with, in the chart legend and tooltip.
 *
 * Total over the union for the same reason every other record here is: a new dated count
 * arrives with no label, and `undefined` in a tooltip is the kind of defect that ships.
 * These are the SERIES names, deliberately shorter than the card titles they sit under
 * ("Reported stolen" the card, "Stolen reports" the line) — a legend row is read beside
 * its own title, so repeating the title verbatim adds nothing.
 */
const DIMENSION_SERIES_LABEL: Record<AnalyticsSeriesDimension, string> = {
  scans: 'Scans',
  certificates: 'Certificates',
  transfers: 'Transfers',
  invalid_scans: 'Invalid scans',
  stolen_reports: 'Stolen reports',
  lost_reports: 'Lost reports',
  resales: 'Resales',
}

/**
 * Whether a dimension's chart can be broken down by certificate status.
 *
 * Exported because the LEGEND is rendered by the card shell's header, outside this
 * component (see `ScansByTimeLegend`), so both ends have to reach the same answer from the
 * same table rather than each deciding for itself.
 */
export const dimensionHasStatusBreakdown = (dimension: AnalyticsSeriesDimension): boolean =>
  DETAILED_FIELD[dimension] !== null

/**
 * The series actually drawn for a given status selection.
 *
 * Empty when nothing is filtered — that case draws the single total area, not a series
 * per status. A series survives the filter if ANY of its status keys was selected,
 * which is what makes "Transfer" one selectable thing: the global filter expands that
 * one option into its four underlying values (CERT_TRANSFER_STATUSES).
 *
 * Exported because the LEGEND is now rendered by the card shell's header, outside this
 * component — so both ends have to derive the same series from the same statuses, and
 * a second copy of this predicate is exactly the drift §36 is about.
 */
export const activeSeriesFor = (statuses: string[]) =>
  statuses.length > 0 ? STATUS_SERIES.filter(s => s.keys.some(k => statuses.includes(k))) : []

/**
 * The series palette, in `STATUS_SERIES` slot order — i.e. one entry per drawn series,
 * each resolved from that series' OWN status through `STATUS_CHART_HUE`.
 *
 * NOT a standalone categorical ramp any more. It used to be six hues chosen purely for
 * separability (`brand[500]`, `cyan[600]`, `green[600]`, `orange[600]`, `pink[600]`,
 * `red[600]`) and assigned by declaration order, which produced a chart whose colours
 * agreed with the status chips on two of six series and contradicted them on the rest.
 * The separability argument that motivated it is preserved — see `STATUS_CHART_HUE` for
 * the all-pairs CVD figures on the new set — but it is now a CONSTRAINT the derived
 * palette had to satisfy rather than the thing that picks the hues.
 *
 * The lookup is by the series' FIRST status key, which is safe because a series is a
 * group of keys that share a LABEL, and every such group shares a badge colour by
 * construction (that is why they share a label). The fallback is `text.secondary` and
 * cannot fire — the record is exhaustive over `CERTIFICATE_STATUSES` — but it is a
 * legible colour rather than `undefined` if the two files ever diverge at runtime.
 *
 * TODO(shared palette): a status→chart-hue mapping belongs beside `CERTIFICATE_STATUSES`
 * in `constants/statuses.ts` once a second chart needs it (§36 — this is the first).
 */
export const useSeriesColors = () => {
  const theme = useTheme()
  return useMemo(
    () =>
      STATUS_SERIES.map(({ keys }) => {
        const hue = STATUS_CHART_HUE[keys[0] as keyof typeof CERTIFICATE_STATUSES]
        return hue ? hue(theme) : theme.palette.text.secondary
      }),
    [theme]
  )
}

/**
 * ── COMPOSED-SERIES COLOURS (VRG-467) — pure function of (theme, index), no store ──
 *
 * A metric dragged onto this chart (see `composedSeries` on `ScansByTimeProps`) draws as
 * an extra line, and the LEGEND for it is rendered by `analyticsDisplayVariants` as a
 * sibling in the card shell's header — the same siblings-not-parent-and-child shape
 * `useHiddenSeries` and `useChartMark` solve with a module store, EXCEPT this one needs
 * no store at all: nothing here is toggled, hidden or overridden by the user, so the
 * chart and the legend can each call this pure function with the same `(theme, index)`
 * and get byte-identical answers. A shared store would be solving a synchronisation
 * problem that does not exist.
 *
 * `cyan`/`pink` — deliberately outside the families `STATUS_CHART_HUE` already spends
 * (gray/green/blue/orange/red) and outside `brand[500]` (the unfiltered total's own
 * colour), so a composed series is never mistaken for a status line or the total.
 */
export const COMPOSED_SERIES_HUES: ((t: Theme) => string)[] = [
  t => t.palette.cyan[600],
  t => t.palette.pink[600],
]

export const composedSeriesColor = (theme: Theme, index: number): string =>
  COMPOSED_SERIES_HUES[index % COMPOSED_SERIES_HUES.length](theme)

/** The card's title. Exported: the shell draws the header, so the page needs it. */
export const scansByTimeTitle = (dataType: AnalyticsSeriesDimension) =>
  `${DIMENSION_SERIES_LABEL[dataType]} by time`

/**
 * The clause naming WHAT is counted, per dimension.
 *
 * A record rather than a sentence built from `DIMENSION_SERIES_LABEL`, because the verb
 * differs and the difference carries the meaning: a certificate is CREATED, a scan is
 * RECORDED, and a stolen report is a dated EVENT rather than a current state — the last
 * being the distinction its ROI card spends two lines on, so the chart must not quietly
 * contradict it. Total over the union, so a new count cannot ship with no description.
 */
const DIMENSION_DESCRIPTION: Record<AnalyticsSeriesDimension, string> = {
  scans: 'Scan volume over the selected date range.',
  certificates: 'Certificates created over the selected date range.',
  transfers: 'Ownership transfers started over the selected date range.',
  invalid_scans:
    'Scans rejected as invalid over the selected date range. The scan status and certificate status filters do not narrow this count, so it can differ from the scans series.',
  stolen_reports:
    'Certificates reported stolen over the selected date range. Counts the dated report event, not the number currently in a stolen state.',
  lost_reports:
    'Certificates reported lost over the selected date range. Counts the dated report event, not the number currently in a lost state.',
  resales:
    'Ownership transfers completed as a sale over the selected date range. Gifts and a brand’s first handoff to an owner are excluded.',
}

/**
 * One descriptive line under the title (§40). The zoom hint is folded in here rather
 * than rendered as a second, competing subtitle — a card carries one.
 */
export const scansByTimeDescription = (dataType: AnalyticsSeriesDimension) =>
  `${DIMENSION_DESCRIPTION[dataType]} Drag the handles below the chart to zoom in.`

// ---------------------------------------------------------------------------
// Series visibility — the legend's local view state
// ---------------------------------------------------------------------------

/**
 * WHY AN EXTERNAL STORE AND NOT `useState` + PROPS, OR A CONTEXT.
 *
 * The legend and the chart are SIBLINGS, not parent and child: `AnalyticsPage` renders
 * `<ScansByTimeLegend>` into the card shell's header `action` slot and `<ScansByTime2>`
 * into the shell's body. So there is no component that owns both, and the two ordinary
 * answers are both unavailable — lifting state needs a common owner (that is
 * `AnalyticsPage`, another agent's file this pass), and a Context needs a Provider above
 * both, which is the same file.
 *
 * `useSyncExternalStore` is React's own primitive for exactly this shape: shared state
 * with no provider. The store is module-private, so nothing outside this file can read or
 * write it — which is the property that matters here, because the whole point of this
 * feature is that toggling a series changes NOTHING beyond this card.
 *
 * `getSnapshot` must return a referentially STABLE value or `useSyncExternalStore` loops
 * forever, so `state` is replaced only on a real change and never rebuilt per call.
 */
interface HiddenSeriesState {
  /** The series set this hidden list was recorded against — see `useHiddenSeries`. */
  sig: string
  hidden: readonly string[]
}

const EMPTY_HIDDEN: readonly string[] = []

const hiddenSeriesStore = (() => {
  let state: HiddenSeriesState = { sig: '', hidden: EMPTY_HIDDEN }
  const listeners = new Set<() => void>()
  const emit = () => listeners.forEach(fn => fn())
  return {
    subscribe(fn: () => void) {
      listeners.add(fn)
      return () => {
        listeners.delete(fn)
      }
    },
    getSnapshot: () => state,
    toggle(sig: string, dataKey: string) {
      // A toggle against a DIFFERENT series set starts from empty rather than mutating a
      // stale list — the same reset the readers derive, applied on the write path so the
      // two cannot disagree.
      const base = state.sig === sig ? state.hidden : EMPTY_HIDDEN
      const hidden = base.includes(dataKey)
        ? base.filter(k => k !== dataKey)
        : [...base, dataKey]
      state = { sig, hidden }
      emit()
    },
  }
})()

/**
 * The hidden set for a given series selection, plus its toggle.
 *
 * ## The reset is DERIVED, not an effect
 *
 * A hidden list recorded against one series set means nothing against another, so a
 * snapshot whose `sig` does not match is simply ignored and the caller reads empty. That
 * is the same mechanism `brushWindow` uses a few hundred lines down, and for the same
 * reason: an effect-based reset runs one frame LATE, so the render that first shows the
 * new selection would draw it with the previous selection's series still hidden.
 *
 * ## Why the signature is the SERIES SET and not the raw statuses
 *
 * Four status keys collapse into the single "Transfer" series (see `STATUS_SERIES`), so
 * keying on the raw status array would reset visibility on a filter change that alters
 * nothing the reader can see. Keying on the drawn series resets exactly when the legend's
 * own rows change, which is what "the selection changed" means from this card's side.
 */
const useHiddenSeries = (activeSeries: { dataKey: string }[]) => {
  const sig = useMemo(() => activeSeries.map(s => s.dataKey).join('|'), [activeSeries])
  const snapshot = useSyncExternalStore(hiddenSeriesStore.subscribe, hiddenSeriesStore.getSnapshot)
  const hidden = snapshot.sig === sig ? snapshot.hidden : EMPTY_HIDDEN
  const toggle = useCallback((dataKey: string) => hiddenSeriesStore.toggle(sig, dataKey), [sig])
  return { hidden, toggle }
}

/**
 * The legend exists for every FILTERED view and for none of the unfiltered one.
 *
 * Unfiltered there is one series and the card title names it ("Scans by time"), so
 * a legend box would repeat the title — billing has none for the same reason. But
 * a single SELECTED status is not that case: it wears its own slot colour rather
 * than `brand[500]`, and the title still says "Scans", so without a legend row
 * nothing on this card says which status the line is. Hence `>= 1`, not `> 1`.
 *
 * The swatch reads the same `seriesColors` slot the line does, so the two cannot
 * drift, and the LABEL carries identity — colour is never the only channel.
 *
 * A component rather than a node computed inside the chart, because it now lives in
 * the shell's header action slot while the chart lives in the shell's body.
 *
 * ## It is the chart's REAL legend now — clicking a row hides that series
 *
 * Before this it was a passive echo of the Filters drawer: every row it drew was a
 * restatement of a chip the user had already selected two controls away, so it consumed
 * a header slot to tell them something they had just said. The choice was to delete it or
 * to make it earn the space; toggling is the standard thing a legend does, it is the one
 * interaction a reader of a multi-series chart actually wants (isolate a line, compare a
 * pair), and it is not otherwise reachable — the drawer can only remove a status from the
 * QUERY, which refetches and rescales the axis, whereas hiding is instant and leaves the
 * scale alone so the remaining lines do not move under the comparison.
 *
 * ## Toggling deliberately does NOT write the global filter
 *
 * Same rule, and the same reasoning, as the brush (see this file's header): a control
 * inside one card must not silently rewrite `filters.*`, which scopes the KPI row above
 * this card and the map below it. Hiding a series is a LOCAL VIEW act — it changes which
 * of the fetched series are drawn and nothing else. Re-coupling it to the filter would
 * reproduce defect (3) exactly, plus a new one: removing a status from the query changes
 * the y-axis domain, so "hide this line to see the others better" would rescale the very
 * lines it was meant to isolate.
 *
 * The consequence to be aware of, stated rather than hidden: the hidden set is NOT part
 * of the saved view (`TableViewState`) and is not meant to be. It is ephemeral, like the
 * brush window and like card expansion.
 */

/**
 * ── A CALL-SITE-SCOPED SIZE OVERRIDE, NOT AN EDIT TO `StatusPill` ──────────────────
 *
 * Product direction: this card's status pill read TOO BIG next to the certificate-status
 * FILTER pill elsewhere on this page (the applied-filter chip, `TagChip` in
 * `AnalyticsAppliedFilters` — measured live at 13px/500/28px tall). `StatusPill`'s own
 * `pill` variant renders at 38px (`px: 4, py: 2.5` on the 4px scale = 16px/10px padding
 * around a 16px icon), which is 10px taller than the target.
 *
 * ⚠️ `StatusPill` IS SHARED — its OTHER caller is the Filters drawer
 * (`StatusChipFilter`), whose own comment calls its size a deliberate "roomier tap
 * target in the filter drawer". Shrinking the component's `pill` variant globally would
 * shrink every drawer chip too, which is a real, wider regression this task was not
 * asked for and has no drawer-side approval. So this stays a wrapper override, scoped to
 * this one render, rather than a `StatusPill.tsx` edit — the §36/§46 "shared component"
 * escape hatch for exactly this situation.
 *
 * Specificity, not `!important`: `StatusPill`'s own padding is one class selector on the
 * button itself (`.css-x-MuiStack-root { padding: ... }`, specificity 0,1,0). This rule's
 * selector is `.css-y button` — one class plus one element type (0,1,1) — which beats it
 * regardless of CSS insertion order. The icon (16px, via `StatusPill`'s own `& svg` rule)
 * and the label font (13px, already matching the target) are left alone — only the
 * padding that inflates the pill to 38px is touched. New height, worked from the same
 * 16px icon: `16 + 2×5 (py) + 2×1 (border)` = 28px, i.e. exactly `TagChip`'s height.
 */
const SCANS_STATUS_PILL_SIZE_SX = {
  '& button': {
    paddingLeft: '10px',
    paddingRight: '10px',
    paddingTop: '5px',
    paddingBottom: '5px',
  },
} as const

export const ScansByTimeLegend = ({
  statuses = [],
  /**
   * The chart's dimension — needed only to decide whether this legend applies AT ALL.
   *
   * VERA-260: four of the seven chartable dimensions have no per-status breakdown
   * (`DETAILED_FIELD`), and without this the legend would still draw a full row of status
   * chips beside them — chips that toggle series which are not on the chart. That is the
   * dead-control failure §46 is about, in its most misleading form: the control looks
   * live, responds to a click, and changes nothing.
   *
   * Defaulted to `'scans'` so the existing call sites that pass only `statuses` keep their
   * exact behaviour rather than silently losing their legend.
   */
  dimension = 'scans',
}: {
  statuses?: string[]
  dimension?: AnalyticsSeriesDimension
}) => {
  const theme = useTheme()
  const seriesColors = useSeriesColors()
  const activeSeries = useMemo(
    () => (dimensionHasStatusBreakdown(dimension) ? activeSeriesFor(statuses) : []),
    [statuses, dimension],
  )
  const { hidden, toggle } = useHiddenSeries(activeSeries)

  if (activeSeries.length === 0) return null

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      gap={2}
      sx={SCANS_STATUS_PILL_SIZE_SX}
    >
      {/*
        `Display:` — the same label pattern `RoiValueChart`'s legend and
        `AnalyticsMapGroupingToggle`'s "Group by:" both use: `variant="body3"`,
        `text.secondary`, no-wrap, in the row with the controls it names. This row's
        pills ARE controls (`onClick={toggle}`, `aria-pressed`), so the colon is honest
        here for the same reason it is on those two — a bare row of colour swatches
        reads as decoration until a reader has already discovered they are clickable.
      */}
      <Typography variant="body3" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
        Display:
      </Typography>
      {activeSeries.map(({ label, slot, keys, dataKey }) => {
        const isHidden = hidden.includes(dataKey)
        const status = CERTIFICATE_STATUSES[keys[0] as keyof typeof CERTIFICATE_STATUSES]
        return (
          /**
           * THE SAME PILL THE FILTERS DRAWER RENDERS (§28/§36).
           *
           * Not a lookalike built here — `StatusPill` is the extracted, shared
           * implementation and the drawer renders it too, so the two surfaces cannot
           * drift. This is the whole point: a reader who has learned that a filled amber
           * "Lost" chip means Lost in the drawer reads the same chip the same way here.
           *
           * ## SHOWN maps to SELECTED, and that is the right way round
           *
           * `selected={!isHidden}`. In the drawer a selected chip means "this status is
           * IN" — in the query; here it means "this status is IN" — in the chart. Both are
           * the present, full-colour, participating state, so the mapping is the identity
           * one and needs no inversion. A hidden series takes the unselected treatment:
           * neutral border, no fill, plain label. Reversing it would make the pills a
           * reader has learned mean the opposite of what they mean two controls away.
           *
           * That swap is also a far stronger signal than what it replaces — this used to
           * be `opacity: 0.4`, which is a dimming a low-contrast display can swallow.
           * Hidden vs shown now differs in fill, border AND label colour.
           *
           * ## The colours: ONE source, TWO steps, and the dot is what ties them
           *
           * The pill's fill/border/label come from `CERTIFICATE_STATUSES` via `statusTone`
           * — the same entry `STATUS_CHART_HUE` derives the LINE's hue from, so the pill
           * and the line can never disagree about which family a status belongs to. They
           * are deliberately different STEPS of it (a chip is read on a tinted ground, a
           * 2px stroke on a card surface — see `STATUS_CHART_HUE`).
           *
           * Which is exactly why the swatch is pinned with `iconColor`: the legend's one
           * job is "which line is which", and only the line's EXACT stroke can answer it.
           * `seriesColors[slot]` is the same value the `<Line>`/`<Area>` is given, read
           * from the same array, so the dot cannot drift from the mark it names.
           *
           * `slot`, not the index in this filtered list, so a status keeps its colour
           * whatever subset is selected.
           */
          <StatusPill
            key={label}
            option={{
              label,
              color: status.color,
              backgroundColor: status.backgroundColor,
              borderColor: status.borderColor,
              // Hollow when hidden — a SECOND channel beside the selected/unselected
              // treatment, so the state never rests on colour alone.
              icon: (
                <Circle
                  sx={{
                    color: isHidden ? 'transparent' : seriesColors[slot],
                    borderRadius: '50%',
                    boxShadow: isHidden ? `inset 0 0 0 2px ${seriesColors[slot]}` : 'none',
                  }}
                />
              ),
            }}
            iconColor={seriesColors[slot]}
            selected={!isHidden}
            onClick={() => toggle(dataKey)}
            // Names the THING, not the state — the state is `aria-pressed`'s job, and
            // announcing both is how a toggle ends up read out contradicting itself.
            ariaLabel={`${label} series`}
            tooltip={isHidden ? `Show ${label}` : `Hide ${label}`}
          />
        )
      })}
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// The MARK — line or bar, and who decides
// ---------------------------------------------------------------------------

/* ────────────────────────────────────────────────────────────────────────────────────
   VALUE SERIES — formatting, and the tooltip that has to carry both terms (VERA-260)
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * A value in its own unit. TOTAL over `AnalyticsValueUnit`, so a unit added there is a
 * compile error here rather than a number rendered on whatever axis it landed next to.
 *
 * `cents` is divided ONCE, here, at the render boundary — the wire and every intermediate
 * carry integer cents, which is what stops the 100x error the pricing module exists to
 * prevent. `ratio` keeps two decimals because "2 items per owner" and "2.17" are different
 * claims and the second is the one the arithmetic supports.
 */
const formatValue = (value: number, unit: AnalyticsValueUnit): string => {
  switch (unit) {
    case 'percent':
      return `${value.toFixed(1)}%`
    case 'cents':
      return `$${(value / 100).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`
    case 'days':
      return `${value.toFixed(1)} ${value === 1 ? 'day' : 'days'}`
    case 'ratio':
      return value.toFixed(2)
  }
}

/** The axis label — the same units, shortened, since an axis has no room for a suffix. */
const formatValueAxisTick = (value: number, unit: AnalyticsValueUnit): string => {
  switch (unit) {
    case 'percent':
      return `${value}%`
    case 'cents':
      return `$${Math.round(value / 100).toLocaleString()}`
    case 'days':
      return `${value}d`
    case 'ratio':
      return String(value)
  }
}

/**
 * ── THE TOOLTIP IS WHERE TRAPS 1 AND 3 ARE ANSWERED, SO IT IS NOT THE DEFAULT ONE ────
 *
 * recharts' built-in tooltip prints `name: value` and nothing else, which for a rate is
 * three separate failures at once:
 *
 *  1. A BARE PERCENTAGE IS UNREADABLE. "50.0%" does not say whether it rests on two
 *     observations or two thousand, and on a per-bucket series those are both common. This
 *     is the rule the scan choropleth already follows — it never shows a percentage
 *     without `numerator of denominator` beside it — restated for the time axis.
 *  2. A SUPPRESSED POINT WOULD SIMPLY VANISH. The built-in tooltip lists the series
 *     recharts drew, and a null point is not drawn, so hovering a low-confidence bucket
 *     would show an empty tooltip identical to an empty bucket's. This one reads the raw
 *     statistic off the row (`STATISTIC_KEY`) instead of off the drawn series, so it can
 *     say WHICH of the two happened and, for a suppression, what the threshold was.
 *  3. NOTHING WOULD SAY THE SERIES DOES NOT SUM TO THE CARD. The reconstruction sentence
 *     is on the card's description, but a reader comparing a hovered point against the
 *     figure above it is doing that comparison HERE. So the `bucket-local` case — the one
 *     where the per-bucket reading is a different question rather than a component — is
 *     repeated in the tooltip, and only that case, because a note on every hover is a note
 *     nobody reads.
 */
const ValueTooltip = ({
  active,
  payload,
  label,
  dimension,
}: {
  active?: boolean
  payload?: readonly { payload?: Record<string, any> }[]
  label?: string
  dimension: AnalyticsValueDimension
}) => {
  const theme = useTheme()
  const row = payload?.[0]?.payload
  if (!active || !row) return null

  const statistic = row[STATISTIC_KEY] as
    | { value: number | null; numerator: number | null; denominator: number }
    | undefined
  const unit = analyticsValueUnit(dimension)
  const terms = analyticsValueTerms(dimension)
  const reconstruction = analyticsValueReconstruction(dimension)

  const denominator = statistic?.denominator ?? 0
  const plotted = isPlottableStatistic(statistic)

  /*
    THREE STATES, SAID DIFFERENTLY — the whole reason this component exists. Collapsing
    any two of them is the defect: "nothing happened" and "too little to plot" and "here
    is the figure" are three different things a gap in a line cannot distinguish.
  */
  const body = plotted ? (
    <>
      <Typography variant="body3" sx={{ color: 'text.primary', fontWeight: 600 }}>
        {formatValue(statistic!.value as number, unit)}
      </Typography>
      <Typography variant="body4" sx={{ color: 'text.secondary' }}>
        {terms.numerator !== null && statistic!.numerator !== null
          ? `${statistic!.numerator.toLocaleString()} ${terms.numerator} of ${denominator.toLocaleString()} ${terms.denominator}`
          : `median of ${denominator.toLocaleString()} ${terms.denominator}`}
      </Typography>
    </>
  ) : denominator === 0 ? (
    <Typography variant="body4" sx={{ color: 'text.secondary' }}>
      No {terms.denominator} in this period, so there is no figure to show.
    </Typography>
  ) : (
    <Typography variant="body4" sx={{ color: 'text.secondary' }}>
      Not shown — {denominator.toLocaleString()} {terms.denominator} is too few to measure
      a reliable figure (needs {ANALYTICS_MIN_STATISTIC_DENOMINATOR}).
    </Typography>
  )

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        boxShadow: theme.shadows[2],
        px: 3,
        py: 2,
        maxWidth: 260,
      }}
    >
      <Typography variant="body4" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        {label}
      </Typography>
      <Typography variant="body4" sx={{ color: 'text.secondary', display: 'block' }}>
        {analyticsValueLabel(dimension)}
      </Typography>
      {body}
      {/* Only the bucket-local case, and only when there IS a figure to be misread. See
          this component's header for why this is not shown on every hover. */}
      {plotted && reconstruction === 'bucket-local' && (
        <Typography
          variant="body4"
          sx={{ color: 'text.secondary', display: 'block', mt: 1, lineHeight: 1.5 }}
        >
          Measured within this period only — not a share of the figure above the chart.
        </Typography>
      )}
    </Box>
  )
}

export type ScansByTimeMark = 'line' | 'bar'

/**
 * The DEFAULT mark is a line, at every bucket count. Bar is one click away in the toggle.
 *
 * ## What this replaced, and why the replacement is not a regression
 *
 * Until VRG-512 this was `bucketCount <= 14 ? 'bar' : 'line'`, and the argument for the
 * bar half was a good one, inherited from `BillingOverview`: a monotone curve drawn
 * through a handful of discrete totals interpolates values that never existed. Its example
 * is `0, 0, 0, 1, 0, 2` — as a smoothed area that reads as gently rising activity, which
 * is the opposite of what happened; as bars it reads "three months of nothing, then one,
 * then none, then two", which is the truth.
 *
 * That argument is still correct about SPARSE data. It stopped being the right default for
 * two reasons, one of them created by this same ticket:
 *
 *   1. ⚠️ THE GRANULARITY CHANGE INVERTED WHAT THE RULE SELECTS FOR. `analyticsChartGranularity`
 *      now buckets anything past ~3 months by MONTH. A twelve-month range is therefore 12
 *      buckets, and 12 is under the old threshold of 14 — so the rule that existed to catch
 *      a handful of sparse points would have made BARS the default for a full year of data,
 *      and for every range longer than it. The threshold was measured against a world where
 *      a long range meant many daily buckets. It no longer does, and a count-based rule
 *      cannot tell "5 buckets because there is little data" from "12 buckets because the
 *      buckets are months".
 *   2. A chart whose FORM changes as the reader moves the date picker is itself a cost. The
 *      old docblock recognised this — it is why the rule keyed on a count rather than on
 *      the values — but a count is not stable either once the bucket WIDTH is variable.
 *      One default at every range means the reader learns one shape.
 *
 * The honesty concern the bar rule existed to protect is not discarded, it is relocated:
 * the toggle still offers Bar, and the reader looking at a genuinely sparse series can say
 * so. What is given up is that they must now say it rather than have it said for them.
 * That is the trade product asked for and it is worth naming rather than burying.
 */
const defaultMarkFor = (): ScansByTimeMark => 'line'

/**
 * The mark's two facts: what the RULE says, and what the USER said instead.
 *
 * Two fields rather than one, because collapsing them loses the ability to keep obeying
 * the rule. If the toggle simply wrote `mark`, then the first time a user touched it the
 * automatic default would be dead for the session — including after a date change that
 * takes the chart from 30 daily buckets to 5. Keeping the override separate means it
 * layers ON TOP of a rule that is still running underneath.
 *
 * Same `useSyncExternalStore` shape, and for the same reason, as `hiddenSeriesStore`
 * above: the toggle renders into the card SHELL's header while the chart renders into the
 * shell's body, so the two are siblings with no common owner below `AnalyticsPage`.
 *
 * `auto` is written by the chart (it is the only one that knows the bucket count) from an
 * effect, never during render — a render-phase store write is how you get a tearing
 * warning and a double render.
 */
interface MarkState {
  auto: ScansByTimeMark
  override: ScansByTimeMark | null
}

const DEFAULT_MARK_STATE: MarkState = { auto: 'line', override: null }

/**
 * ── KEYED BY CARD ID, NOT A SINGLE SHARED STATE (VERA-260) ──────────────────────────
 *
 * This used to be one `MarkState`, module-wide. With VRG-481's card unification a reader
 * can place several `Total Scans` / `Total Certificates` chart cards on one canvas at
 * once — each is a distinct `AnalyticsCardItem`, identified by its
 * `analyticsVariantCardId` — and a single shared state made toggling one card's mark
 * retarget every other chart card on the page. So the store is a `Map<cardId, MarkState>`
 * and every read/write is scoped to the card that owns it; the two-fact (`auto`/
 * `override`) design above is unchanged, just per id now instead of singular.
 *
 * `emit()` still notifies every subscriber regardless of which id changed — the same
 * shape as before this change — but `getSnapshot` returns the SAME object reference for
 * an id whose state didn't move, so `useSyncExternalStore`'s `Object.is` check means a
 * card unaffected by another card's toggle does not re-render. Cheap enough at the ~4
 * chart cards this page can hold that a per-id listener registry isn't worth the
 * complexity.
 *
 * ## Keyed by card id, NOT lifted into the card itself
 *
 * The chart and its `ScansByTimeMarkToggle` are siblings (see the original docblock
 * above), so there is no single React node that could own this as local state without a
 * lift to `AnalyticsPage` — the same reason `hiddenSeriesStore` a few hundred lines up is
 * a module store rather than `useState`. Keying the EXISTING store by id, rather than
 * inventing a second mechanism, keeps this consistent with that precedent.
 *
 * ## Deliberately NOT persisted into the saved view (`useAnalyticsView`)
 *
 * An override is a display PREFERENCE for a card instance, so "persist it like the
 * card's other display choices" is the plausible alternative — but `useAnalyticsView`
 * already establishes the pattern this follows instead: `hiddenSeriesStore` (this file)
 * and `removedPlacement` (`useAnalyticsView.ts`) are BOTH session-scoped, ephemeral,
 * outside `currentState`, and explicitly documented as such. The mark override joins
 * them for the same reason stated in this file's own `defaultMarkFor` docblock: the auto
 * mark is a claim about how the data should be READ, and
 * an override persisted alongside a saved view would outlive the range that
 * justified it — a view saved at 5 buckets (bar, by the rule) and reopened months later
 * at 90 buckets would still force bars over a range the rule would now draw as a line,
 * with no visible reason why. Session-only means the override answers "what do I want to
 * look at RIGHT NOW", never "what did I once decide about data that no longer exists".
 * If product direction later wants overrides to survive a reload, that is new persisted
 * state on `AnalyticsViewState` (a `cardMarks: Record<string, ScansByTimeMark>` next to
 * `cards`/`layouts`) — not a retrofit of this store, which is deliberately kept
 * outside `useAnalyticsView`'s comparison the same way its two precedents are.
 */
const markStore = (() => {
  const state = new Map<string, MarkState>()
  const listeners = new Set<() => void>()
  const emit = () => listeners.forEach(fn => fn())
  const getState = (cardId: string): MarkState => state.get(cardId) ?? DEFAULT_MARK_STATE
  return {
    subscribe(fn: () => void) {
      listeners.add(fn)
      return () => {
        listeners.delete(fn)
      }
    },
    getSnapshot: getState,
    setAuto(cardId: string, auto: ScansByTimeMark) {
      const current = getState(cardId)
      if (current.auto === auto) return
      state.set(cardId, { ...current, auto })
      emit()
    },
    setOverride(cardId: string, override: ScansByTimeMark) {
      const current = getState(cardId)
      if (current.override === override) return
      state.set(cardId, { ...current, override })
      emit()
    },
  }
})()

/**
 * `[effective mark, set an override]` for ONE card. Effective = the user's choice for
 * `cardId`, else that card's rule-derived default. See the `markStore` docblock above for
 * why this is keyed rather than singular.
 */
const useChartMark = (cardId: string): [ScansByTimeMark, (next: ScansByTimeMark) => void] => {
  const getSnapshot = useCallback(() => markStore.getSnapshot(cardId), [cardId])
  const { auto, override } = useSyncExternalStore(markStore.subscribe, getSnapshot)
  const set = useCallback((next: ScansByTimeMark) => markStore.setOverride(cardId, next), [cardId])
  return [override ?? auto, set]
}

const MARK_OPTIONS: readonly ViewToggleOption<ScansByTimeMark>[] = [
  { value: 'line', label: 'Line', icon: ChartLineIcon },
  { value: 'bar', label: 'Bar', icon: ChartColumnIcon },
]

/**
 * The line/bar control, for the card shell's header `action` slot.
 *
 * ## Placement
 *
 * The `action` slot, NOT the card's top-right corner. That corner already carries the drag
 * grip and the expand button and has been called cramped; the `action` slot is where this
 * page's other panel — the map — already puts its All scans / Latest location control, so
 * this is the established home for a card-level view control rather than a third
 * convention.
 *
 * ## ⚠️ `ViewToggle`, NOT `SegmentedTabs` — and this replaced a `SegmentedTabs`
 *
 * The segmented pill's volume is for a choice that reshapes the whole screen. Line-vs-bar
 * changes no number anywhere — it is a per-card view preference — so drawing it with the
 * same recessed track, sliding pill and 48px band claimed a weight it does not carry.
 * `ViewToggle` is the quiet form; its docblock holds the full argument. `RoiValueChart`
 * uses the identical option pair, so the two charts still read as one system (§28/§36).
 *
 * ⚠️ This note used to cite "the page's Scans / Certificates toggle, which changes every
 * figure on screen" as the counter-example. That control no longer exists: it never
 * changed every figure (the KPI tiles ignored it), and it now renders in this very header
 * as `AnalyticsDimensionToggle` — in the quiet form, for the same reason this is.
 *
 * Icons rather than words because line and bar HAVE conventional glyphs. Scans /
 * certificates and the map's All scans / Latest location do not, so they take the same
 * component with text labels — matching the weight, not the mechanism.
 *
 * ## Sharing the slot with the legend
 *
 * The legend and this control are both header-right, laid out as one row. The legend is
 * variable-width (one chip per selected status, up to six) and the toggles are fixed, so
 * the fixed ones sit to its right and it grows leftwards into the space it needs — the
 * inverse arrangement makes them move every time a status is selected.
 *
 * The DIMENSION toggle is rightmost, and this one gave up that position to it. See
 * `AnalyticsPage`'s panel definitions for why: the map's controls are ordered by the same
 * rule, and there the rightmost slot is the only one that does not shift when the mode
 * toggle hides in certificates mode.
 *
 * ## `cardId` — required, no default (VERA-260)
 *
 * The toggle and its chart are siblings (see the `markStore` docblock above), so the
 * caller — `analyticsDisplayVariants.tsx` — must pass the SAME id it gives the
 * `ScansByTime2` it pairs this with, or the two halves silently read/write different
 * cards' state. Required rather than optional so a missing id is a type error, not a
 * card that quietly falls back to sharing state with every other unkeyed card.
 */
export const ScansByTimeMarkToggle = ({ cardId }: { cardId: string }) => {
  const [mark, setMark] = useChartMark(cardId)
  return (
    <ViewToggle options={MARK_OPTIONS} value={mark} onChange={setMark} ariaLabel="Chart type" />
  )
}

/** Collapsed height of the plot. Expanded, the chart fills the region instead. */
const CHART_HEIGHT = 460

/** How long a series' entry animation runs, and how far apart the series are staggered. */
const ENTRY_DURATION_MS = 800
const ENTRY_STAGGER_MS = 80

const MS_DAY = 24 * 60 * 60 * 1000

/*
  ── WHERE THE BUCKET WIDTH IS DECIDED (VRG-512) ─────────────────────────────────────

  Nowhere in this file. `analyticsEffectiveBucketUnit` (components/analytics/analyticsDisplay)
  resolves the reader's "View by:" choice against the range — falling back to the
  range-derived unit when they have expressed none, or when the range cannot honour the one
  they did — and `analyticsBucketRequest` turns that unit into the (count, granularity)
  pair this component spreads into the request.

  ⚠️ A LOCAL `HOURLY_MAX_SPAN_MS` ALIAS USED TO LIVE HERE AND IT IS DELETED ON PURPOSE.
  Importing the shared THRESHOLD while re-deriving the DECISION beside it looked like
  reuse and was not: the moment that function learned to step past `day`, this chart went
  on sending `isHours ? 'hour' : 'day'` and a 12-month range kept drawing ~365 points,
  while the CSV export — which called the function — was already correct. A constant is
  only a single source for the thing it actually holds; the decision has to be imported
  too, or the two drift in exactly the direction that leaves the chart wrong and the
  export right.
*/

interface ScansByTimeProps {
  /**
   * The card's own identity (`analyticsVariantCardId(metricId, 'chart')`) — required so
   * this instance's line/bar mark (`useChartMark`/`markStore`) is scoped to IT and not
   * shared with every other chart card on the canvas. Must match the id passed to the
   * paired `ScansByTimeMarkToggle` in the same card's header. See the `markStore`
   * docblock (VERA-260) for the incident this fixes.
   */
  cardId: string
  period: string
  /** Certificate statuses from the global filter header. Empty = all statuses. */
  globalCertificateStatuses?: string[]
  /** Date range from (falls back to last `period` days when not provided) */
  dateFrom?: Date
  /** Date range to (falls back to now when not provided) */
  dateTo?: Date
  /**
   * Whether to plot scans data or certificates data. Defaults to 'scans'.
   *
   * Typed as `AnalyticsSeriesDimension` — the store's own DERIVED dimension union — rather
   * than restating a literal union inline. `analyticsDisplay` derives whether a metric may
   * be drawn as a chart partly FROM this union (`CHART_RENDERABLE` is a total `Record` over
   * it), so a new dated count added to the statistics response is a compile error there
   * until someone says whether this component can plot it. An inline copy would have let
   * the two drift and the capability answer go stale in the direction that lies.
   *
   * ⚠️ WIDER THAN `AnalyticsDataType` since VERA-260, and that asymmetry is deliberate:
   * the map and the leaderboard stay on the narrow union because their endpoints do. See
   * the note on `AnalyticsDataType` itself.
   */
  dataType?: AnalyticsSeriesDimension
  /**
   * ── VRG-467: EXTRA METRICS COMPOSED ONTO THIS CHART, drawn as flat TOTAL lines ──────
   *
   * Each entry is another metric a reader dragged onto this card. It draws as ONE line
   * per entry — the metric's total-per-bucket count, `renderedSeries`' `TOTAL_KEY`
   * treatment extended to a second dimension — deliberately WITHOUT a per-status
   * breakdown: `STATUS_SERIES`' certificate-status grouping is a property of the PRIMARY
   * metric's own filter, and combining two different metrics' per-status breakdowns on
   * one legend answers a question nobody asked ("scans that are Active" is not a
   * measurement). A composed series is a comparison at the TOTAL level: "how do these two
   * counts move together", which is what the drag was asking for.
   *
   * ⚠️ NO SECOND FETCH. `useAnalyticsStatistics`' response already carries `scans_count`
   * AND `certificates_count` on every bucket regardless of which `dataType` was asked
   * for — see `GetAnalyticsStatisticsPayload`'s own note ("the chart is NOT affected…
   * `dataType` only picks which one `ScansByTime2` reads, client-side"). So a composed
   * dimension is read straight off the SAME `statistics` response this component already
   * has; issuing a second `useAnalyticsStatistics` call here would be requesting data
   * that already arrived, and — the reason that would be a real bug, not just waste —
   * would write into the SAME single redux slot the primary fetch reads, racing it (the
   * exact hazard `useAnalyticsRoi`/`useAnalyticsRoiHero` were split over).
   *
   * ⚠️ SHARED AXIS, NO SCALING. All SEVEN composable metrics (VERA-260 — the two counts
   * this note used to name, plus transfers, invalid scans, stolen and lost reports and
   * resales) are plain counts, so one y-axis is still correct as-is — see
   * `analyticsCardDimension`'s dimension registry, which is what a compose drop is
   * capability-gated against in the first place (`AnalyticsCanvas.isComposeDropTarget`).
   * A metric with a DIFFERENT unit (a currency ROI figure, say) becoming composable would
   * need a second, independently-scaled axis before this can honestly plot it alongside a
   * count — nothing here builds that speculatively; this note is so the next person who
   * wires a currency metric into the compose grammar finds the constraint before shipping
   * a chart that plots dollars and counts on one scale.
   *
   * ⚠️ MAGNITUDES CAN DIVERGE FAR MORE THAN THEY USED TO, which is a real reading hazard
   * even though it is not a correctness one. Scans and certificates were within an order
   * of magnitude of each other; 6,172 scans composed against 2 lost reports puts the
   * second series flat on the axis. It is honest — that IS the comparison the reader
   * asked for by dragging — and the fix, if one is wanted, is the same second axis the
   * paragraph above describes rather than any rescaling of the data.
   */
  composedSeries?: readonly {
    metricId: string
    dataType: AnalyticsSeriesDimension
    label: string
  }[]
  /**
   * ── VERA-260: PLOT A STATISTIC INSTEAD OF A COUNT ───────────────────────────────────
   *
   * When set, this card draws ONE value series — a rate, a mean or a median per bucket —
   * and `dataType` is ignored. The two are alternatives, not layers, and the type system
   * already keeps them apart: `AnalyticsSeriesDimension` and `AnalyticsValueDimension` are
   * asserted disjoint in `store/analytics/types`, so no card can resolve to both.
   *
   * ⚠️ NO STATUS BREAKDOWN, NO COMPOSITION, AND NEITHER IS A GAP.
   *
   * A per-status split of a RATE is not this measurement — "the invalid scan rate of
   * Active certificates" is a differently-filtered rate, not a component of this one, and
   * unlike counts the parts do not reassemble into the whole. So `activeSeries` is forced
   * empty in this mode, exactly as it is for the four count dimensions that carry no
   * `_detailed` twin.
   *
   * Composition is refused for the reason `composedSeries` above already anticipates in
   * its own note: "a metric with a DIFFERENT unit […] would need a second,
   * independently-scaled axis before this can honestly plot it alongside a count". A
   * percentage, a money mean and a median in DAYS are three units, and none of them shares
   * the count axis. Rather than build a second axis speculatively, a value card simply
   * offers no compose target — which is what `analyticsCardDimension` returning `null` for
   * these ids already produces, since that is what `isComposeDropTarget` gates on.
   *
   * ⚠️ NO SECOND FETCH, same as the composed count series. The statistics response already
   * carries all six statistics on every bucket regardless of which card asked, so this
   * reads the response this component already has. Issuing another `useAnalyticsStatistics`
   * here would race the primary fetch into the single redux slot they share.
   */
  valueDimension?: AnalyticsValueDimension
  /** Certificate owner ids from the global filter header. Empty = all owners. */
  ownerIds?: string[]
  /** Product ids from the global filter header. Empty = all products. */
  productIds?: string[]
  /** Certificate ids from the global filter header. Empty = all certificates. */
  certificateIds?: string[]
  /** Scan locations (country) from the global filter header. Empty = all locations. */
  location?: string[]
  /**
   * True when this card owns the whole content region. The plot then FILLS its box
   * instead of taking the fixed collapsed height — the one thing expanding a chart is
   * for. Everything else about the card is unchanged.
   */
  expanded?: boolean
}

// Recharts Brush onChange payload
interface BrushChangeEvent {
  startIndex?: number
  endIndex?: number
}

/**
 * The local view window, tagged with the identity of the data it was drawn against.
 *
 * Tagging (rather than clearing the window from an effect) is what makes the reset
 * happen DURING the render that first shows new data, instead of one frame later. An
 * effect-based reset would let a stale window — possibly indexing past the end of a
 * shorter dataset — reach recharts for a frame first.
 */
interface BrushWindow {
  sig: string
  start: number
  end: number
}

// Hoisted so the Brush's props are referentially stable across renders. recharts
// compares chart children with a shallow prop equality (ReactUtils.isSingleChildEqual)
// and re-derives the chart's axis/offset/stack state whenever they differ — an inline
// `() => ''` would make the Brush unequal on EVERY render and pay that cost each time.
const NO_BRUSH_TICKS = () => ''

/** One size for BOTH axes' tick labels, so the two can never drift apart (the house size). */
const AXIS_TICK_FONT_SIZE = CHART_AXIS_TICK_FONT_SIZE

/**
 * ── THE PLOT RECTANGLE IS FLUSH WITH THE CARD'S CONTENT BOX ─────────────────────────
 *
 * `left: 0` and `right: 0`, and the y-axis is `mirror`ed (see the `<YAxis>` below), so
 * `offset.left` and `offset.right` are both ZERO and the plotted line begins and ends
 * exactly where the card's own content begins and ends — under the left edge of the
 * header's title and the right edge of its controls.
 *
 * It used to be `{ left: 10, right: 30 }` with a 36px-wide y-axis in the gutter, which
 * put the plot's left edge 46px inside the card's content box and its right edge 30px
 * inside. Every control ABOVE the chart (the title, the promote row's Reset / Apply to
 * page buttons) sits at the content edge, so the chart was visibly indented from the
 * things it belongs to.
 *
 * ── `bottom` IS THE DEAD BAND BELOW THE BRUSH, AND IT IS NOW ZERO ───────────────────
 *
 * `bottom` is NOT the gap to the brush — that is the `<XAxis height>`, see its note. It is
 * the empty band BELOW the brush, and it is exactly `margin.bottom` px tall. Read off the
 * source rather than inferred: `calculateOffset` sets `brushBottom = offset.bottom` while
 * that still equals `margin.bottom + xAxis.height`, then adds the brush's own height to
 * `offset.bottom`; `renderBrush` places the brush at
 * `offset.top + offset.height + offset.brushBottom - margin.bottom`, which reduces to
 * `svgHeight - margin.bottom - brushHeight`. So the brush's bottom edge sits
 * `margin.bottom` px above the bottom of the `<svg>`, and the `<svg>` is flush with the
 * card's content box.
 *
 * At 40 that produced 65px of white between the brush and the card's outer edge (40 dead
 * band + the card's 24px padding + 1px border) against a 25px left/right gutter — the
 * asymmetry that was reported. At 0 the brush ends where the content box ends, so the
 * visible gap below it IS the card's padding, 25px, matching the sides exactly (§22 makes
 * the same argument for a full-page table).
 *
 * Nothing else moves: the 40px comes back as PLOT height (`offset.height` grows by it),
 * the x-axis band that separates the plot from the brush is `<XAxis height>` and is
 * untouched, and the tick labels are placed from the plot's baseline, not the band's.
 *
 * Hoisted for the same reason as `NO_BRUSH_TICKS`: `margin` is compared with
 * `shallowEqual` in generateCategoricalChart's `getDerivedStateFromProps`, so a stable
 * object is free insurance against that comparison ever tightening.
 */
const PLOT_MARGIN = { top: 10, right: 0, left: 0, bottom: 0 }

/** Props recharts passes to a custom axis `tick` renderer, narrowed to what is used. */
interface AxisTickProps {
  x?: number
  y?: number
  /** For an x-axis this is `offset.width` — the plot's width. */
  width?: number
  /** recharts sets a tick's `fill` from the axis' own `stroke`. */
  fill?: string
  payload?: { value?: string | number }
}

/**
 * X-axis tick that stays inside the plot at its edges.
 *
 * Now that the plot runs edge to edge (`PLOT_MARGIN`), the first and last ticks can sit ON
 * the boundary. Recharts centres every x tick and offers no per-tick escape hatch, so a
 * centred label at either end hangs half outside the `<svg>` and is CLIPPED — the
 * regression that the old 10px/30px margins were quietly paying for.
 *
 * ## It CLAMPS, it does not re-anchor — and the difference is not cosmetic
 *
 * The first version switched `textAnchor` to `start`/`end` at the edges. That is a
 * HALF-LABEL shift, whether or not half a label is what was overflowing, and it produced a
 * worse defect than the one it fixed: on a 3-month range, "Aug 28 2026" overflowed by
 * 0.6px, got anchored `end`, jumped ~31px left and landed on top of "Aug 18 2026".
 * Recharts thins ticks by `minTickGap` using their CENTRED positions, so any post-thinning
 * shift can manufacture a collision that the thinning had already ruled out.
 *
 * Clamping the centre into `[halfWidth, width - halfWidth]` moves a label by exactly the
 * amount it overflows and no more — 0.6px in that case, a full half-width only when the
 * tick really is on the boundary. Ticks that fit are untouched, which matters for bars: a
 * band scale insets the first and last tick by half a band, so nothing overflows and every
 * label keeps the centre that puts it under its own bar.
 *
 * The geometry comes from what recharts already passes down: `x` is the tick's position
 * and `width` is the plot width. The plot starts at 0 because `PLOT_MARGIN.left` is 0 and
 * a mirrored y-axis contributes nothing to `offset.left` (`calculateOffset` skips mirrored
 * axes outright).
 *
 * Module level, not an inline arrow — see `NO_BRUSH_TICKS`.
 */
const EdgeAwareXTick = ({ x = 0, y = 0, width = 0, fill, payload }: AxisTickProps) => {
  const label = String(payload?.value ?? '')
  // An ESTIMATE, and deliberately so: it only decides how far a label must move to stop
  // overflowing, and measuring each label in the DOM would cost a layout per tick per
  // render. 0.52em per glyph is the average advance of this stack at 11px.
  const halfWidth = (label.length * AXIS_TICK_FONT_SIZE * 0.52) / 2
  const clampedX = Math.min(Math.max(x, halfWidth), Math.max(width - halfWidth, halfWidth))
  return (
    <text
      x={clampedX}
      y={y}
      // recharts' own `Text` takes `verticalAnchor="start"` and converts it to a shift;
      // a raw `<text>` positions by BASELINE, so `0.71em` is the equivalent — it puts the
      // cap height just under `y` and keeps the labels exactly where they already sat.
      dy="0.71em"
      textAnchor="middle"
      fill={fill}
      fontSize={AXIS_TICK_FONT_SIZE}
    >
      {label}
    </text>
  )
}

export const ScansByTime2 = ({
  cardId,
  period,
  globalCertificateStatuses = [],
  dateFrom,
  dateTo,
  dataType = 'scans',
  composedSeries = [],
  valueDimension,
  ownerIds = [],
  productIds = [],
  certificateIds = [],
  location = [],
  expanded = false,
}: ScansByTimeProps) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const seriesColors = useSeriesColors()

  /**
   * ── WHY THE SCAN-STATUS FILTER GETS NO LEGEND ROW HERE (AND NO CAVEAT EITHER) ──────
   *
   * This card USED to render a caveat line saying the Scan status filter did not reach
   * it. That was true and is no longer: the chart's scan series was moved off
   * `certificate_action_logs` and onto the `scans` table (VERA-260), `useAnalyticsStatistics`
   * now injects `scan_statuses`, and the filter narrows this chart exactly as it narrows
   * the map. The caveat was deleted with the defect it described.
   *
   * The original ask — draw the selected scan-status pills in THIS card's legend, beside
   * the certificate-status ones — was declined at the time BECAUSE the chart could not
   * honour the filter. That reason is gone, so the question was reopened, and the answer
   * is still no. Two reasons survive the fix, and neither depended on the old one:
   *
   * 1. A LEGEND ROW HERE IS A TOGGLE OVER A SERIES. Every existing row names one drawn
   *    line and hides it on click. A scan status names no line — it is a narrowing
   *    already applied to ALL of them, server-side. A pill in that row would either do
   *    nothing on click (a dead control among live ones, indistinguishable because it is
   *    the same component) or mutate the PAGE-GLOBAL filter, which is a categorically
   *    different scope from "hide this line locally" wearing the same clothes.
   *
   * 2. IT IS ALREADY SAID, TWICE, A FEW INCHES UP. The toolbar chip and the applied-filter
   *    chip row both name the selected outcomes. Repeating them in-card would make this
   *    the only card that restates a page filter — and every other filter (owner, product,
   *    location, date) narrows this chart with no in-card echo at all. Adding one for scan
   *    status alone implies the others do not apply.
   *
   * So: nothing is rendered. That is the correct end state rather than an omission — a
   * filter that simply works needs no annotation, and the annotation that used to be here
   * existed only because it did not.
   */

  const from = dateFrom ?? (() => {
    const d = new Date()
    d.setDate(d.getDate() - parseInt(period, 10))
    return d
  })()
  const to = dateTo ?? new Date()

  /*
    `period` now only supplies the FALLBACK range above; it no longer decides granularity.

    ⚠️ THE BUCKET IS DERIVED, NOT INLINED (VRG-512). This used to compute `isHours` here
    and send `isHours ? 'hour' : 'day'` straight into the request, which meant the shared
    `analyticsChartGranularity` was the single source for the 48-hour THRESHOLD while this
    component kept its own copy of the DECISION — so when that function learned to step
    past `day`, this chart carried on asking for daily buckets and a 12-month range drew
    ~365 points. The fix is to consume the function rather than re-derive beside it; the
    export already did, which is why the export would have been right and the chart wrong.
  */
  const spanMs = to.getTime() - from.getTime()
  /*
    THE UNIT IS THIS CARD'S READER-CHOSEN ONE IF THEY EXPRESSED ONE, ELSE THE RANGE'S.

    `null` (no entry for this card) means Automatic — so the range-derived unit remains what
    almost every reader sees, and this stays a chart nobody has to configure.

    ⚠️ KEYED BY `cardId`, AND IT USED TO BE ONE PAGE-LEVEL REDUX FIELD (VRG-512). The
    bucket is a display property of ONE card, exactly like the line/bar mark two hundred
    lines up: measured on a canvas holding a Scans chart and a Certificates chart, the
    page-level field made one "View by" change fire two requests and re-bucket both. So the
    store is keyed the same way `markStore` is, and read through the same
    `useSyncExternalStore` shape — see `analyticsCardGranularity` for the full argument,
    including why a MODULE store rather than component state (the CSV export has to read
    this card's bucket, and it is a plain async function).
  */
  const [granularityChoice] = useAnalyticsCardGranularity(cardId)
  const bucketUnit = analyticsEffectiveBucketUnit(from, to, granularityChoice)
  const bucket = analyticsBucketRequest(bucketUnit)
  const isHours = bucketUnit === 'hour'
  /**
   * An hourly axis over MORE than one day needs the day in the label. `HH:mm` alone is
   * unique inside a 24h window and ambiguous outside one — "14:00" would appear twice on a
   * two-day range with nothing to say which day is which, and the tooltip inherits the same
   * label, so the ambiguity would follow the reader into the hover.
   */
  const hourLabelIncludesDay = isHours && spanMs > MS_DAY

  // VRG-467 — a STRING signature of which dimensions are composed, not the `composedSeries`
  // array itself: the array is a fresh literal from the caller (`analyticsDisplayVariants`,
  // built from a card's parsed id) on every render, so depending on it directly would rebuild
  // `chartData` below on every render regardless of whether anything actually changed.
  const composedSig = composedSeries.map(s => s.dataType).join('|')

  const { data: statistics, isLoading } = useAnalyticsStatistics({
    from,
    to,
    ...bucket,
    company_ids: [DEFAULT_COMPANY_ID],
    certificate_statuses: globalCertificateStatuses.length > 0 ? globalCertificateStatuses : undefined,
    owner_ids: ownerIds.length > 0 ? ownerIds : undefined,
    product_ids: productIds.length > 0 ? productIds : undefined,
    certificate_ids: certificateIds.length > 0 ? certificateIds : undefined,
    location: location.length > 0 ? location : undefined,
  })

  const chartData = useMemo(() => {
    const rows: Record<string, any>[] = []
    statistics?.data.forEach((item: any) => {
      const record: Record<string, any> = {
        /*
          THE LABEL FOLLOWS THE BUCKET, or the axis lies about what a mark covers.

          A month bucket labelled `Mar 04 2026` reads as that DAY's figure rather than the
          month's — and it is a whole month of scans, so the reader is off by ~30x with
          nothing on screen to warn them. Same for a year. This mirrors
          `RoiValueChart.bucketLabel`, which switches on its own granularity for the
          identical reason.

          WEEK deliberately keeps the plain dated label: a week bucket is labelled by its
          START date, which a reader can find on a calendar, where an ISO week number
          ("W34") is unreadable to anyone not already thinking in them — the same call
          `RoiValueChart` made and worth keeping identical across the two charts.
        */
        name: isHours
          ? hourLabelIncludesDay
            ? date(item.timestamp, 'MMM d, HH:mm')
            : time(item.timestamp)
          : bucketUnit === 'year'
            ? date(item.timestamp, 'yyyy')
            : bucketUnit === 'month'
              ? date(item.timestamp, 'MMM yyyy')
              : date(item.timestamp),
        // Kept on the row (rather than in a parallel `useState` array populated from
        // inside this `useMemo` — a render-phase side effect that cost an extra render
        // and could serve a stale array on the first pass) so a brush index maps to a
        // real timestamp with no second source to keep in step.
        _timestamp: item.timestamp,
      }
      /*
        The per-status breakdown, WHEN THE DIMENSION HAS ONE — a total `Record` lookup,
        not the old `dataType === 'certificates' ? … : 'scans_detailed'` ternary, whose
        else-branch silently claimed every unknown dimension was scans. See
        `DETAILED_FIELD`'s own note: that default is exactly the wrong-series-under-the-
        right-title failure that kept the other dimensions unchartable.
      */
      const detailedField = DETAILED_FIELD[dataType]

      if (detailedField) {
        const counts: Record<string, number> = {}
        item[detailedField]?.forEach((entry: any) => {
          counts[entry.status] = entry.count
        })

        // Per-series totals. A series is a LABEL (see STATUS_SERIES), so the four
        // transfer states sum into one value rather than becoming four lines.
        let total = 0
        STATUS_SERIES.forEach(({ dataKey, keys }) => {
          const sum = keys.reduce((acc, key) => acc + (counts[key] ?? 0), 0)
          record[dataKey] = sum
          total += sum
        })
        // The all-status total is the single series drawn when nothing is filtered.
        // Summed from the same per-status counts rather than read off a separate
        // field, so the total and the breakdown can never disagree.
        record[TOTAL_KEY] = total
      } else {
        /*
          No breakdown: the total comes straight off the bucket's own count field — the
          SAME field, read the same way, that makes this dimension's series sum to the ROI
          card beside it. This is also the only path that can produce a non-zero total for
          these four; falling through to the branch above would have summed a status
          breakdown that does not exist and drawn a flat zero line, which reads as "no data
          in this range" rather than as a bug (the silent failure `analyticsDisplay`'s
          header names as the most dangerous kind).

          Every `STATUS_SERIES` key is still WRITTEN, at zero. The rendered children list is
          fixed-length by design (see `renderedSeries`) and recharts rekeys by position, so
          a row missing keys the series list references would not shorten the list — it
          would feed `undefined` into series that are `hide`den anyway. Zero is the honest
          value for "this status is not a fact about this measurement", and it costs six
          numbers a row.
        */
        STATUS_SERIES.forEach(({ dataKey }) => {
          record[dataKey] = 0
        })
        record[TOTAL_KEY] = item[TOTAL_COUNT_FIELD[dataType]] ?? 0
      }

      // VRG-467 — composed series, read straight off THIS SAME bucket's other count
      // fields. No second fetch, no per-status breakdown — see the prop's own note.
      composedSeries.forEach(({ dataType: composedDataType }, index) => {
        record[composedDataKey(index)] = item[TOTAL_COUNT_FIELD[composedDataType]] ?? 0
      })

      /*
        ── VERA-260: THE VALUE POINT, AND THE TWO WAYS IT CAN BE ABSENT ────────────────

        🚨 `null`, NEVER `?? 0`. Every other assignment in this function coalesces a
        missing count to zero, which is correct for a count — a bucket with no scans had
        zero scans. It is WRONG here and wrong in the direction that lies: a bucket with no
        scans has no invalid-scan RATE, and plotting 0 asserts "0% invalid", which is the
        opposite reading from "nothing happened". With `connectNulls` off (recharts'
        default, made explicit on the `<Area>` below) a null breaks the line into segments
        instead of drawing a straight interpolation across a value nobody measured.

        `isPlottableStatistic` folds in the second absence — a denominator too small to
        support the figure — so both produce a gap. THE RAW STATISTIC IS STILL WRITTEN TO
        THE ROW regardless, which is what lets the tooltip tell the two apart; see
        `STATISTIC_KEY`.
      */
      if (valueDimension) {
        const statistic = item[valueDimension]
        record[STATISTIC_KEY] = statistic
        record[VALUE_KEY] = isPlottableStatistic(statistic) ? statistic.value : null
      }

      rows.push(record)
    })
    return rows
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `composedSig`, not
    // `composedSeries`, is the intentional dependency; see its own declaration above.
  }, [statistics, isHours, hourLabelIncludesDay, dataType, composedSig, valueDimension])

  /**
   * The series actually drawn.
   *
   * Empty when nothing is filtered — that case draws the single total area, not a
   * series per status. A series survives the filter if ANY of its status keys was
   * selected, which is what makes "Transfer" one selectable thing: the global filter
   * expands that one option into its four underlying values (CERT_TRANSFER_STATUSES).
   */
  /*
    ⚠️ GATED ON THE DIMENSION (VERA-260), and this is load-bearing rather than tidy.

    `activeSeries.length === 0` is what selects the single-total-area mode below. A
    dimension with no per-status breakdown must ALWAYS be in that mode — otherwise a
    reader who has a certificate-status filter set (a page-level filter, nothing to do with
    this card) would get an invalid-scans chart drawn as per-status series that are all
    zero, while its real total sat unplotted on the same row. Empty here is what routes it
    to the total.

    The status filter still narrows the QUERY for these dimensions, which is correct and
    unchanged: the server scopes the whole bucket by it. What is refused is the client-side
    SPLIT of a measurement that has no status axis.
  */
  const activeSeries = useMemo(
    () =>
      // VERA-260 — a VALUE series never splits by certificate status. "The invalid scan
      // rate of Active certificates" is a differently-filtered rate, not a component of
      // this one, and unlike counts the parts do not reassemble into the whole. Same
      // routing as the four count dimensions with no `_detailed` twin: empty here is what
      // selects the single-series mode below.
      !valueDimension && dimensionHasStatusBreakdown(dataType)
        ? activeSeriesFor(globalCertificateStatuses)
        : [],
    [globalCertificateStatuses, dataType, valueDimension]
  )

  /**
   * What the legend has hidden — read from the same module store the legend writes, since
   * the two components are siblings rather than parent and child (see `useHiddenSeries`).
   *
   * `visibleSeries` is kept SEPARATE from `activeSeries` on purpose. `activeSeries.length
   * === 0` is what selects the single-total-area mode below, and that must keep meaning
   * "the user has filtered nothing". Filtering the hidden set into that same variable
   * would make "hide every status" fall through to drawing the all-status total — i.e.
   * hiding all the lines would draw MORE data than leaving them on, and it would look
   * identical to having cleared the filter. Hiding everything correctly yields an empty
   * plot with its axes intact, and every legend row dimmed to say why.
   */
  const { hidden: hiddenSeries } = useHiddenSeries(activeSeries)
  const visibleSeries = useMemo(
    () => activeSeries.filter(s => !hiddenSeries.includes(s.dataKey)),
    [activeSeries, hiddenSeries]
  )

  // ---------------------------------------------------------------------------
  // Local view window
  // ---------------------------------------------------------------------------

  const [brushWindow, setBrushWindow] = useState<BrushWindow | null>(null)

  // Identity of the plotted series. Changes whenever the query behind the chart
  // changes (a new date range, a new filter, a different dataType), which is exactly
  // when a window drawn over the OLD series stops meaning anything.
  const dataSig = useMemo(
    () =>
      [
        chartData.length,
        chartData[0]?._timestamp ?? '',
        chartData[chartData.length - 1]?._timestamp ?? '',
        dataType,
      ].join('|'),
    [chartData, dataType]
  )

  const lastIndex = Math.max(0, chartData.length - 1)
  // Derived, not stored: a window tagged with a different signature is simply ignored,
  // so the brush is back at the full domain on the very first render of new data.
  const activeWindow = brushWindow && brushWindow.sig === dataSig ? brushWindow : null
  const startIndex = activeWindow ? Math.min(activeWindow.start, lastIndex) : 0
  const endIndex = activeWindow ? Math.min(activeWindow.end, lastIndex) : lastIndex
  const isWindowed = chartData.length > 1 && (startIndex > 0 || endIndex < lastIndex)

  // Read inside a stable callback so the handler identity never changes — see the
  // NO_BRUSH_TICKS note on why a fresh Brush prop each render is not free.
  const dataSigRef = useRef(dataSig)
  dataSigRef.current = dataSig

  // Purely local. No dispatch, so a mid-drag burst of these costs nothing beyond a
  // re-render of this card — the whole point of the decoupling.
  const handleBrushChange = useCallback(({ startIndex: s, endIndex: e }: BrushChangeEvent) => {
    if (s === undefined || e === undefined) return
    setBrushWindow({ sig: dataSigRef.current, start: s, end: e })
  }, [])

  const resetWindow = useCallback(() => setBrushWindow(null), [])

  // The ONE promote path. Fires on click, never during a drag, and writes the global
  // filter exactly once — after which the page (KPIs, map, this chart) refetches for
  // the new range and the brush reopens across the full width of it.
  const applyWindowToPage = useCallback(() => {
    const selectedFrom = new Date(chartData[startIndex]?._timestamp ?? from)
    // A row's `_timestamp` is the START of its bucket (midnight for a day, the top of
    // the hour for an hour), so passing the last selected row's own timestamp as `to`
    // would cut that bucket off at its first instant and silently drop everything the
    // user can see plotted in it. Take the NEXT bucket's start, minus a millisecond;
    // when the window already runs to the end of the series there is no next bucket,
    // so the page's existing range end is already correct.
    const nextBucket = chartData[endIndex + 1]?._timestamp
    const selectedTo =
      endIndex < lastIndex && nextBucket ? new Date(new Date(nextBucket).getTime() - 1) : to
    if (isNaN(selectedFrom.getTime()) || isNaN(selectedTo.getTime())) return
    dispatch(setAnalyticsFilters({ dateRange: { from: selectedFrom, to: selectedTo } }))
    setBrushWindow(null)
  }, [chartData, startIndex, endIndex, lastIndex, from, to, dispatch])

  const windowLabel =
    isWindowed && chartData[startIndex]?.name && chartData[endIndex]?.name
      ? `${chartData[startIndex].name} to ${chartData[endIndex].name}`
      : null

  // ---------------------------------------------------------------------------
  // The mark, and the one series that may carry an area
  // ---------------------------------------------------------------------------

  // Read-only here — the header's `ScansByTimeMarkToggle` owns the write. Scoped to THIS
  // card's id so a sibling chart card's mark can't leak in (see the `markStore` docblock).
  const [mark] = useChartMark(cardId)

  /**
   * Publish the RULE's answer, so the toggle in the card header can show the effective
   * mark without knowing anything about the data.
   *
   * ⚠️ NO LONGER KEYED ON THE BUCKET COUNT (VRG-512). `defaultMarkFor` is a constant now
   * — see its docblock for why the sparse-bars rule was retired — so this publishes once
   * per card rather than on every range change. The auto/override seam is kept rather
   * than deleted: `useChartMark` still reads `override ?? auto`, so reinstating a
   * data-dependent default later is a change to ONE function, not a re-plumbing.
   *
   * In an effect, not during render: this writes to a store the toggle is subscribed to,
   * and a render-phase write to an external store is a tearing hazard. The store no-ops
   * when the value is unchanged, so a re-render costs nothing.
   */
  useEffect(() => {
    markStore.setAuto(cardId, defaultMarkFor())
  }, [cardId])

  /**
   * ── THE AREA FILL RULE: fill when, and only when, EXACTLY ONE series is visible ─────
   *
   * Overlapping translucent areas rank by DRAW ORDER, not by value. The topmost fill
   * obscures the ones under it, and where they overlap the colours composite into a hue
   * that matches no legend swatch — so with six statuses the chart would be least readable
   * exactly when it carries the most information. One area has nothing to occlude and
   * nothing to composite with, so it is free.
   *
   * ⚠️ THE CONDITION IS THE VISIBLE-SERIES COUNT, NOT "IS A FILTER APPLIED". That is a
   * behavioural change and not a restatement. It used to key off `activeSeries.length === 0`
   * — unfiltered meant one total series with an area, filtered meant N lines with none —
   * which was sufficient only while applying a filter was the sole route to more than one
   * series. The legend is now clickable, so a user can select three statuses and hide two:
   * they ARE looking at a single series and it should have its fill. The inverse matters
   * too — a lone selected status that is joined by a second must LOSE its fill, or two
   * areas start occluding each other.
   *
   * Three cases, and the third is deliberate:
   *   0 visible with a filter applied → NOTHING is drawn. It must NOT fall through to the
   *     all-status total: hiding every line would then draw MORE data than leaving them on,
   *     and would look identical to having cleared the filter.
   *   1 visible → an area, in THAT SERIES' own colour (never a fixed brand hue — the fill,
   *     the stroke and the legend swatch all have to be the same colour for the single
   *     source to mean anything).
   *   2+       → lines only.
   *
   * The unfiltered total is its own, separate single-series case below: it is not a status,
   * so it has no status hue and keeps `brand[500]`, matching the billing chart.
   *
   * BARS NEVER GET A FILL. A bar is already a filled shape; a gradient area under a bar
   * series is a second mark stating the same thing. So this is gated on `mark === 'line'`.
   */
  const filledSeries = mark === 'line' && visibleSeries.length === 1 ? visibleSeries[0] : null

  /**
   * Gradient ids are per SERIES, not one shared id.
   *
   * A single hardcoded `scansByTimeFill` whose stops now vary by series is exactly the
   * shape that silently keeps the first colour it saw — an SVG paint server is referenced
   * by id, so two charts on one page, or a stale def surviving a re-render, resolve to
   * whichever element owns that id at paint time. Keying the id to the series makes the
   * reference unambiguous, and the `defs` below renders only the one gradient in use.
   */
  const gradientIdFor = (key: string) => `scansByTimeFill-${key.replace(/[^a-zA-Z0-9]/g, '')}`

  /**
   * ── THE SERIES SET IS FIXED. VISIBILITY IS A PROP. ──────────────────────────────────
   *
   * Seven entries — the all-status total plus one per `STATUS_SERIES` slot — ALWAYS, in
   * the same order, whatever the filter says. What a status selection changes is each
   * entry's `hide`, which is a prop, not a mount.
   *
   * ## Why this is the same trick the area fill already uses
   *
   * The fill rule above renders `<Area fill="transparent">` rather than swapping to a
   * `<Line>`, precisely so recharts does not see a different element TYPE and remount the
   * series each time the visible count crosses 1. This is that argument applied one level
   * up, to the LIST: rendering `{cond && <Total/>}` followed by `visibleSeries.map(...)`
   * made both the number and the positions of the chart's children a function of the
   * filter, and recharts flattens its children through `React.Children.toArray`, which
   * DROPS falsy children and rekeys what remains by position. So the `false` left by an
   * unrendered total shifted every status series' generated key by one, and every one of
   * them remounted — a full rebuild for what should have been one line appearing.
   *
   * ## `hide` is a first-class "not participating", not a display trick
   *
   * recharts excludes a hidden item from `getStackGroupsByAxisId` outright, so it takes no
   * part in the y-domain and none in bar-width allocation — a `hide`den bar does not eat a
   * slot and leave a gap. The one thing it keeps is its POSITION IN THE CHILDREN LIST,
   * which is the whole point.
   *
   * This also fixes a defect the old shape had on its own terms: rows carry a value for
   * every status regardless of what was queried, so a series the user has not selected
   * would plot a flat zero if it were merely rendered without a fill. `hide` renders
   * nothing at all.
   */
  const renderedSeries = useMemo(() => {
    const visible = new Set(visibleSeries.map(s => s.dataKey))
    return [
      {
        dataKey: TOTAL_KEY,
        // One lookup, total over the dimension union — the old
        // `=== 'certificates' ? … : 'Scans'` labelled every non-certificates dimension
        // "Scans", which on a resales chart would have been a false claim in the tooltip
        // rather than merely an ugly default.
        label: DIMENSION_SERIES_LABEL[dataType],
        // The total is not a status, so it has no status hue — `brand[500]`, as billing.
        color: theme.palette.brand[500],
        hide: activeSeries.length > 0,
        animationBegin: 0,
      },
      ...STATUS_SERIES.map(({ dataKey, label, slot }) => ({
        dataKey,
        label,
        color: seriesColors[slot],
        hide: !visible.has(dataKey),
        // Staggered by SLOT, so a status's entry delay is its own and does not shuffle
        // when a different status is selected alongside it.
        animationBegin: slot * ENTRY_STAGGER_MS,
      })),
      /**
       * VRG-467 — composed series, ALWAYS visible (no filter interplay — a metric a
       * reader dragged onto this chart is not something the certificate-status filter
       * has any opinion about). Appended AFTER the status entries so a composed series
       * never shifts an existing status series' generated position — see this block's
       * own header on why the LIST's length and order, not just its contents, must stay
       * stable across a filter change; appending here extends that same stability to a
       * compose/decompose gesture, for EVERY series that sits before the one added or
       * removed.
       *
       * ⚠️ Unlike the status entries (fixed-length, `hide`-toggled), THIS array's own
       * length changes with `composedSeries.length` — a genuinely new `<Area>`/`<Bar>`
       * mounts on compose and unmounts on full decompose, rather than an existing one
       * merely un-hiding.
       *
       * ⚠️ THE CONDITION THAT MADE THAT HARMLESS NO LONGER HOLDS (VERA-260). This note
       * used to rest on "only two metrics are chart-capable, so `composedSeries` is 0 or 1
       * entries and a change can only ever be add/remove-the-last-item". There are SEVEN
       * now, so removing composed[0] while composed[1] survives is reachable, and it does
       * what the old note predicted: [1] shifts one position earlier and replays its entry
       * animation. That is cosmetic, not a correctness bug — the data, the colours (keyed
       * on index, so they also shift) and the legend all stay consistent with each other,
       * because both ends derive from the same `composedSeries` array.
       *
       * Recorded rather than fixed, and deliberately: the fix is a keyed children list,
       * which means giving recharts a stable `key` per series and is a change to how the
       * whole list is rendered, not a local one. Worth doing when someone reports the
       * flicker; not worth risking the fixed-length invariant this block exists to protect
       * on a speculative pass.
       */
      ...composedSeries.map(({ label }, index) => ({
        dataKey: composedDataKey(index),
        label,
        color: composedSeriesColor(theme, index),
        hide: false,
        animationBegin: (STATUS_SERIES.length + index) * ENTRY_STAGGER_MS,
      })),
    ]
  }, [visibleSeries, activeSeries.length, dataType, seriesColors, theme, composedSeries])

  /**
   * The ONE series that carries a gradient fill, as a key rather than an object — the
   * total and the statuses are one list now, so the two former cases collapse into one
   * lookup. `filledSeries` above still states the rule; this applies it to the total too.
   */
  const filledKey =
    mark !== 'line' ? null : activeSeries.length === 0 ? TOTAL_KEY : (filledSeries?.dataKey ?? null)

  /**
   * ── A SERIES ANIMATES ITS ENTRY ONCE, THEN GOES STATIC ──────────────────────────────
   *
   * This is the second half of "the new lines animate in without a full chart rebuild",
   * and without it the first half is not observable.
   *
   * recharts injects the chart's `updateId` into every graphical item as `animationId`,
   * and `Area.renderAreaWithAnimation` keys its `<Animate>` on it — `key={"area-" + id}`.
   * `updateId` increments whenever `data !== prevData`, which is EVERY refetch. So a new
   * `chartData` array remounts the `<Animate>` of every series, and with it the `<path>`
   * underneath: the marks a reader is watching are torn down and rebuilt for a change
   * that did not concern them.
   *
   * Once a series has appeared, keeping it animated buys nothing — its own numbers have
   * not changed — and costs the rebuild. So `isAnimationActive` is true only for a series
   * that is ENTERING: one that was not among the series drawn before the most recent
   * change to the visible set. A newly selected status still sweeps in; every series
   * already drawn re-renders statically, same DOM node, new `d`.
   *
   * ## Two wrong versions preceded this one, and both failed the same way
   *
   * (1) A ref holding "what was drawn LAST RENDER", flipped in an effect. The effect
   * commits before the next render, and `useQuerySelect` sets `isLoading` a tick after the
   * filter changes, so: select a status, the series mounts with its entry active, the ref
   * updates, the very next render reads the ref, `isAnimationActive` flips false, recharts
   * swaps `renderAreaWithAnimation` for `renderAreaStatically`, and the entry is cancelled
   * a few milliseconds in. Measured in the browser, not theorised.
   *
   * (2) The same ref, plus a `setTimeout` in the effect to hold the series in an "entering"
   * set for the animation's duration. Worse, because it fails OPEN: React runs an effect's
   * previous cleanup before re-running it, so any re-run clears the pending timer, and a
   * re-run whose `appeared` set is empty arms no replacement. Read straight off the fiber:
   * the entering set was still `['s2']` four seconds after a 1.26s timer should have
   * cleared it — the series was then pinned animation-active forever, which is the very
   * bug this block exists to prevent, applied to one series instead of all of them.
   *
   * ## So: no timer, and no effect. Entering is DERIVED from the last visibility change.
   *
   * A series is entering iff it was absent from the previous set of drawn series. That
   * comparison is made once per actual CHANGE of the set, and the answer then holds until
   * the set changes again — so there is no window in which a later render can revoke it,
   * which is precisely what both earlier versions got wrong.
   *
   * Written during render rather than in an effect, deliberately: an effect commits after
   * the render that mounts the series, so anything it computes is already one render too
   * late (that is failure (1)). This is the standard previous-value-in-a-ref idiom — it is
   * guarded by a value comparison, so it is idempotent and survives StrictMode's double
   * render unchanged, and it reads no state it does not itself own.
   *
   * ⚠️ Known and accepted: the most recently added series stays animation-active until the
   * visible set next changes, so a date change immediately after selecting a status will
   * re-play that ONE line's entry while the others update statically. That is a small
   * inconsistency, not a rebuild, and it self-corrects at the next status change. Closing
   * it would require knowing when the animation actually ended, and recharts' only signal
   * for that (`onAnimationEnd`) never fires where `requestAnimationFrame` does not tick.
   */
  const prevVisibleSigRef = useRef<string>('')
  const enteredOnLastChangeRef = useRef<Set<string>>(new Set())

  /**
   * A signature of WHICH series are drawn — the keys, not the series objects, whose
   * identity also turns over when the theme or the data type changes. Only a change in
   * this string is a change in visibility.
   */
  const visibleSig = renderedSeries
    .filter(s => !s.hide)
    .map(s => s.dataKey)
    .join('|')

  if (visibleSig !== prevVisibleSigRef.current) {
    const before = new Set(prevVisibleSigRef.current ? prevVisibleSigRef.current.split('|') : [])
    enteredOnLastChangeRef.current = new Set(
      (visibleSig ? visibleSig.split('|') : []).filter(k => !before.has(k))
    )
    prevVisibleSigRef.current = visibleSig
  }

  const isEnteringSeries = (dataKey: string, hide: boolean) =>
    !hide && enteredOnLastChangeRef.current.has(dataKey)

  return (
    /**
     * BODY ONLY — no `Card`, no `CardSectionHeader`.
     *
     * `AnalyticsCardShell` draws the surface and the header for this panel
     * (`surface: 'shell'`), so drawing them here too produced a card inside a card.
     * Adopting the shell was therefore a DELETION: the title, the description and the
     * legend moved up to the shell's header slot via `scansByTimeTitle`,
     * `scansByTimeDescription` and `<ScansByTimeLegend>`, all exported from this file
     * so there is still exactly one copy of each.
     */
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* The promote path. Zooming is local, so this row is the only thing that can
          take a brush selection out to the rest of the page, and it does so on a
          click — never mid-drag. Rendered only while the brush is actually narrowed,
          so it costs nothing at rest. */}
      {isWindowed && windowLabel && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={3}
          flexWrap="wrap"
          sx={{
            // No top margin: the shell's header already ends with its own `mb: 4`, and
            // this row is the first thing in the body. It used to sit under a header
            // this component drew itself.
            flexShrink: 0,
            py: 2,
            px: 4,
            borderRadius: 2,
            border: `1px solid ${theme.palette.primary[100]}`,
            backgroundColor: theme.palette.action.hover,
          }}
        >
          <Typography variant="body3" color="text.secondary">
            Viewing {windowLabel}. The rest of the page still shows the full date range.
          </Typography>

          <Stack direction="row" spacing={3} alignItems="center">
            {/* Default (40px) size — the shared Button is never rendered `small` (§60);
                one primary, rightmost, with the secondary to its left (§26/§33). */}
            <Button variant="secondary" onClick={resetWindow}>
              Reset
            </Button>
            <Button variant="primary" onClick={applyWindowToPage}>
              Apply to page
            </Button>
          </Stack>
        </Stack>
      )}

      {/* Expanded, the plot FILLS the region (`flex: 1` in a min-height-0 column) —
          the reason to expand a chart at all. Collapsed it keeps the fixed height, so
          the panel grid's rows stay even. The top margin exists only to separate the
          plot from the promote row when that row is showing; the shell's header
          supplies the gap otherwise. */}
      <Box
        sx={{
          flex: expanded ? 1 : 'none',
          minHeight: 0,
          height: expanded ? 'auto' : CHART_HEIGHT,
          mt: isWindowed && windowLabel ? 4 : 0,
        }}
      >
        {/**
          * ── THE SKELETON IS FOR THE FIRST LOAD ONLY ──────────────────────────────────
          *
          * This is the largest single cause of the "adding a status flashes the chart"
          * report, and it had nothing to do with the series at all.
          *
          * `useQuerySelect` sets `isLoading = true` on EVERY params change, not only the
          * first (`useQuerySelect.ts:37` — it compares the serialised params and flips the
          * flag whenever they differ). Selecting a status changes `certificate_statuses`,
          * so a bare `isLoading ?` swapped the entire `<ResponsiveContainer>` for a grey
          * rectangle and back: the chart, its axes, its brush and all seven series were
          * UNMOUNTED and rebuilt from nothing, which is a flash by construction and no
          * amount of care inside the chart could have prevented it.
          *
          * Gating on there being no data to show is also just §19: skeleton on the first
          * load, keep stale data visible through a background refetch. The old data stays
          * on screen for the ~200ms the request takes and is then replaced in place.
          */}
        {isLoading && chartData.length === 0 ? (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={PLOT_MARGIN}>
              {/* The house gradient (`chartStyle`, BillingOverview's 0.3 to 0), so the
                  two charts are the same object when they are doing the same job. Only
                  the ONE gradient actually in use is emitted, and its id names the series
                  it belongs to; see `gradientIdFor` for why a single shared id is a trap
                  now that the colour varies. */}
              <defs>
                {renderedSeries
                  .filter(s => s.dataKey === filledKey)
                  .map(s => (
                    <ChartAreaGradient
                      key={s.dataKey}
                      id={gradientIdFor(s.dataKey)}
                      color={s.color}
                    />
                  ))}
              </defs>

              {/* Recessive horizontal grid. BillingOverview has none, but it is a
                  card-sized sparkline whose job is shape; this is a 460px chart with
                  a visible value axis, and the rules the grid draws are what let a
                  point be read against that axis across the full width. */}
              <CartesianGrid {...chartGridProps(theme)} />

              <XAxis
                dataKey="name"
                // A custom tick, so the end labels can anchor inward instead of being
                // clipped by the now-flush plot edges — see `EdgeAwareXTick`.
                tick={EdgeAwareXTick}
                // The tick's `fill` IS the axis' `stroke` (recharts sets it from there),
                // and this is the only channel a module-level tick renderer can be given
                // a themed colour through. Safe to repurpose: both the axis line and the
                // tick lines are off, so nothing else is stroked.
                stroke={theme.palette.text.secondary}
                {...CHART_AXIS_PROPS}
                // Daily/hourly ticks are dense and full dates do not fit; thin by gap
                // rather than by a fixed interval so the labels stay legible at any width.
                minTickGap={24}
                /**
                 * ── THIS IS THE GAP BETWEEN THE PLOT AND THE BRUSH ────────────────────
                 *
                 * 44, up from the 30 default. `margin.bottom` is NOT the instrument it
                 * looks like: recharts places the brush at
                 * `offset.top + offset.height + offset.brushBottom - margin.bottom`,
                 * which reduces to `plotBottom + xAxis.height` — the bottom margin
                 * cancels out entirely and only ever adds empty space BELOW the brush.
                 * The x-axis band is the whole of the distance between the plot's
                 * baseline and the brush's top edge, so widening the band is the only
                 * thing that separates them.
                 *
                 * The labels do not move: they are placed from the plot's bottom
                 * (`tickSize + tickMargin` below it), not from the band's bottom.
                 */
                height={44}
              />
              {/* Billing HIDES its y-axis; this one keeps it, deliberately. There the
                  chart sits directly above the figure it summarises, so the axis is
                  redundant. Here the magnitude IS the finding and the tooltip would
                  otherwise be the only way to read it — hiding the axis would mean a
                  reader has to hover every point to learn the scale. `allowDecimals`
                  is billing's, and it matters: scans are counts, and the axis was
                  free to label a bucket "0.5".

                  ── `mirror` IS WHAT LETS THE AXIS STAY AND THE PLOT STILL SPAN THE CARD ──

                  The scale had to remain readable without hovering AND the plot had to
                  reach the card's content edges, and those look mutually exclusive: a
                  left axis reserves `width` px of gutter, which is precisely the 46px
                  the plot used to be indented by. `mirror` dissolves it —
                  `calculateOffset` adds an axis' width to `offset.left` only
                  `if (!entry.mirror && !entry.hide)`, so a mirrored axis reserves
                  NOTHING, and `getTickTextAnchor` flips its labels to `start` so they
                  are drawn 8px INSIDE the plot instead of 8px outside it.

                  So this is not billing's `<YAxis hide>` in disguise. Every tick label
                  the axis drew before is still drawn, same size, same colour, same
                  values, against the same dashed rules — they have moved about 40px to
                  the right. The reader still learns the scale by looking; the objection
                  to hiding the axis (that hovering becomes the only way to read
                  magnitude) does not apply to labels that are still on the page.

                  `width` no longer reserves a gutter, but it is still the axis' own box
                  and `CartesianAxis.render` bails out at `width <= 0`, so it stays. */}
              <YAxis
                mirror
                tick={chartAxisTick(theme)}
                {...CHART_AXIS_PROPS}
                allowDecimals={false}
                width={36}
              />

              <Tooltip
                {...chartTooltipStyle(theme)}
                // BillingOverview's pairing: a hovered BAR wants its column washed, a
                // hovered point on a line wants a vertical rule to read against the axis.
                cursor={chartTooltipCursor(theme, mark === 'bar' ? 'bar' : 'line')}
              />

              {/**
                * ── ONE LIST, FIXED LENGTH, ORDER FIXED ─────────────────────────────────
                *
                * This replaces two separate blocks — `{activeSeries.length === 0 && <total/>}`
                * followed by `visibleSeries.map(...)` — whose combined length and child
                * positions both moved with the filter. See `renderedSeries` for why that
                * was a remount of everything rather than a mount of the one new line.
                *
                * NO FILTER → only the all-status total is un-hidden. As a LINE it is drawn
                * exactly as the billing chart draws its daily range: a `brand[500]` monotone
                * area at strokeWidth 2 over the 0.3→0 gradient, no dots. As BARS, exactly as
                * billing draws its monthly range. `brand[500]` in both forms — the total is
                * not a status, so it has no status hue to inherit.
                *
                * STATUSES SELECTED → the total hides and one mark per selected status shows.
                * A series the legend has hidden stays `hide`, which keeps it out of the
                * y-domain: the reason to hide a line is usually that it dwarfs the others,
                * so leaving it in the domain would defeat the gesture. `hide` rescales,
                * which is the point, and it does so without disturbing the children list.
                *
                * Colour comes from the series' own `slot` (baked into `renderedSeries`), not
                * from a position in a filtered list, so removing a status never repaints the
                * ones that remain (see `STATUS_CHART_HUE`).
                *
                * Bars are GROUPED, not stacked: the reason to select several statuses is to
                * compare them, and a stack answers a different question (the total). Every
                * line is the same 2px weight — the old chart drew the first series at 2 and
                * the rest at 1.5, which made stroke weight a second, meaningless rank
                * encoding on top of colour.
                */}
              {renderedSeries.map(({ dataKey, label, color, hide, animationBegin }) =>
                mark === 'bar' ? (
                  <Bar
                    key={dataKey}
                    hide={hide}
                    dataKey={dataKey}
                    name={label}
                    /**
                     * SOLID, never a gradient — and this is a measurement argument, not a
                     * style preference, so do not "improve" it into the area's treatment.
                     *
                     * A bar's LENGTH is the measurement, and it is read against the
                     * BASELINE. A vertical gradient fading toward that baseline softens the
                     * exact edge the value is read from, which is the one part of a bar that
                     * has to stay definite. The area chart has no such problem, and that is
                     * why it is allowed the gradient: there the LINE carries the value and
                     * the fill is a supporting shape underneath it, so the fill can fade for
                     * free. `BillingOverview` draws the same distinction — gradient on its
                     * area, flat `brand[500]` on its bars.
                     *
                     * If bars read heavy, the cause is GEOMETRY: `maxBarSize` and `radius`
                     * (in `CHART_BAR_PROPS`, spread below) are billing's exact values and are
                     * what lighten them, without weakening the encoding.
                     */
                    fill={color}
                    /**
                     * OFF, matching `BillingOverview`'s bars — and the reason is stronger
                     * than consistency. recharts renders a `Bar`'s rectangles from INSIDE
                     * its `Animate` render-prop, so with animation on there is no `<path>`
                     * in the DOM at all until `requestAnimationFrame` first ticks. A line
                     * degrades gracefully under a throttled rAF (the curve is drawn
                     * immediately and only its dash offset animates); a bar chart renders
                     * as 90 EMPTY groups. Measured in the Claude preview pane, where rAF
                     * fires zero times: 90 `.recharts-bar-rectangle` groups, 0 rects.
                     *
                     * The same class of failure reaches real browsers whenever rAF is
                     * throttled — a backgrounded tab that is then screenshotted, a print
                     * path — so this is not a preview-pane workaround. Lines and areas keep
                     * their 800ms entry, which is unaffected. (`isAnimationActive: false`,
                     * with `radius` and `maxBarSize`, is `CHART_BAR_PROPS`.)
                     */
                    {...CHART_BAR_PROPS}
                  />
                ) : (
                  <Area
                    key={dataKey}
                    hide={hide}
                    {...CHART_AREA_PROPS}
                    dataKey={dataKey}
                    name={label}
                    stroke={color}
                    /* The fill rule (see `filledSeries` / `filledKey`): the gradient only
                       when this is the ONE visible series, otherwise a transparent fill,
                       which makes this `Area` behave exactly as the `Line` it replaces.
                       Using `Area` in both cases rather than switching component keeps
                       recharts from remounting the series — and therefore replaying its
                       800ms entry animation — every time the count crosses 1. */
                    fill={dataKey === filledKey ? `url(#${gradientIdFor(dataKey)})` : 'transparent'}
                    fillOpacity={1}
                    // A surface-coloured ring, not a hardcoded white one — on the dark
                    // theme the old `#fff` dot was a bright hole punched in the line.
                    activeDot={chartActiveDot(theme, color)}
                    /* Entry animation for a series that was NOT drawn last render, static
                       for one that was — see `isEnteringSeries`. This is what stops a
                       refetch's `updateId` bump from re-keying every `<Animate>` and
                       rebuilding every `<path>` on screen. */
                    isAnimationActive={isEnteringSeries(dataKey, hide)}
                    animationDuration={ENTRY_DURATION_MS}
                    animationBegin={animationBegin}
                  />
                )
              )}

              {/* CONTROLLED brush. `startIndex`/`endIndex` are driven from local state,
                  which is what makes zoom-out reachable: recharts' chart re-reads a
                  Brush child's indices whenever the children compare unequal
                  (generateCategoricalChart's getDerivedStateFromProps →
                  ReactUtils.isChildrenEqual), so pushing 0..lastIndex here reopens it.
                  Uncontrolled, the traveller positions lived only in chart state and
                  nothing outside a drag could widen them. */}
              {/*
                NO `x` / `width`. The brush spans the PLOT AREA, and that is not a default
                being tolerated — it is the correct extent, so it is left to recharts.

                `renderBrush` in `generateCategoricalChart` clones the Brush with
                `x: isNumber(props.x) ? props.x : offset.left` and
                `width: isNumber(props.width) ? props.width : offset.width`. `offset.left` /
                `offset.width` ARE the plot rectangle — the same numbers `<CartesianGrid>`
                draws to. So omitting both props makes the brush begin exactly where the
                plotted line begins and end exactly where it ends, which is what a
                span-selector over that line has to do.

                A previous version overrode them to `{ x: 0, width: <measured container> }`,
                reading "full width of the graph" as the full width of the SVG. That put the
                brush's left edge 46px LEFT of the first data point, out in the Y-axis label
                gutter, so the traveller you dragged to "the start" sat under the axis
                labels rather than over the data. It also cost a `useState` + a callback ref
                + a `ResizeObserver` to carry one number recharts already computes — all
                deleted with it, which is the real tell that the override was working
                against the library rather than with it.
              */}
              <Brush
                dataKey="name"
                height={28}
                stroke={theme.palette.grey[200]}
                fill={theme.palette.grey[50]}
                travellerWidth={8}
                startIndex={startIndex}
                endIndex={endIndex}
                onChange={handleBrushChange}
                tickFormatter={NO_BRUSH_TICKS}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  )
}
